import { useParams } from "react-router-dom";
import { io } from "socket.io-client";
import React, { useEffect, useRef, useState, useMemo, useReducer } from "react";
import { observerReducer, isObserverVisible, initialObserverState } from './observerPresence';
import BookLoaderScreen from './BookLoaderScreen';
import ViewSlider from './ViewSlider';
import Whiteboard from './Whiteboard';
import CalculatorModeSlider from './CalculatorModeSlider';
import CalculatorWindow from './CalculatorWindow';
import BasicCalculator from './BasicCalculator';
import GeoGebraCalculator from './GeoGebraCalculator';
import './newModeratorLayout.css'; // KOMPLETT NEUES LAYOUT
import './modstyle.css'; // FÜR FRAGEN-MANAGEMENT-PANEL
import { getIceServers } from './iceConfig';
import { withRetry, emitAsync } from './streamUtils';
import { stabilizeQuestions } from './questionShuffle';

export default function ModView() {
  // ========== HOOKS MÜSSEN OBEN SEIN (React Rules of Hooks) ==========
  const { roomId } = useParams();
  const socket = useRef(null);
  const mediasoupSocket = useRef(null);
  const moderatorVideoRef = useRef(null);
  const studentVideoRefs = useRef([]); // Array für Video-Elemente (wird über Callback Refs befüllt)
  const studentAudioElements = useRef({}); // Map studentNumber -> Audio element for unmuted audio playback
  const studentStreamsRef = useRef({}); // studentNumber -> MediaStream (persistent storage for recovery)
  const questionsRef = useRef([]); // Spiegelt 'questions' fuer Socket-Handler (Re-Emit beim Connect)

  // ========== STATE DECLARATIONS (ALL BEFORE useEffect) ==========
  // Authentifizierungs-States
  const [authValidated, setAuthValidated] = useState(false);
  // Ladeanimation-Aufbau mindestens einmal komplett gezeigt (siehe BookLoaderScreen)
  const [loaderMinShown, setLoaderMinShown] = useState(false);
  const [authUser, setAuthUser] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const [authError, setAuthError] = useState(null);

  const getAuthHeaders = () => authToken
    ? { 'Authorization': `Bearer ${authToken}` }
    : {};

  // Fragen States
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [wrongCount, setWrongCount] = useState(0);
  const [showQuestionManager, setShowQuestionManager] = useState(false);
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [studentAnswers, setStudentAnswers] = useState({}); // { studentId: { answer, correct, studentNumber, timestamp } }
  const [studentAnswer, setStudentAnswer] = useState(null); // Legacy compatibility
  const [multiDetailOpen, setMultiDetailOpen] = useState(false); // Aufklapp-Zustand multiDragdrop-Fehlerdetails
  const [studentScores, setStudentScores] = useState({}); // { studentNumber: { correct: 0, wrong: 0 } }
  const [connectedStudents, setConnectedStudents] = useState([]);
  const [roomCapacity, setRoomCapacity] = useState("0/4");
  const [showQuestionBrowser, setShowQuestionBrowser] = useState(false);
  const [browserSelectedQuestion, setBrowserSelectedQuestion] = useState(null);
  const [calculatorMode, setCalculatorMode] = useState('off'); // Schüler-Freischaltung
  const [audioMuted, setAudioMuted] = useState(false); // eigenes Mikrofon stummgeschaltet
  const [videoMuted, setVideoMuted] = useState(false); // eigene Kamera aus (Schüler sehen Kurt)
  const [streamStarted, setStreamStarted] = useState(false); // Mute erst nach Stream-Start möglich
  const [ownBasicOpen, setOwnBasicOpen] = useState(false);     // eigenes Basic-Fenster
  const [ownFullOpen, setOwnFullOpen] = useState(false);       // eigenes Vollwertig-Fenster
  const [calcMenuOpen, setCalcMenuOpen] = useState(false);     // Rechner-Untermenü (Popover)
  
  // Fragen-Manager State
  const [questionManagerView, setQuestionManagerView] = useState('main'); // 'main', 'stapel', 'einzelfragen', 'erstellen'
  const [stapelListe, setStapelListe] = useState([]);
  const [einzelfragenListe, setEinzelfragenListe] = useState([]);
  const [stapelSuche, setStapelSuche] = useState('');
  const [fragenSuche, setFragenSuche] = useState('');
  const [filterSchwierigkeit, setFilterSchwierigkeit] = useState('');
  const [filterKlasse, setFilterKlasse] = useState('');
  const [filterFach, setFilterFach] = useState('');
  const [neueFrageForm, setNeueFrageForm] = useState({
    text: '',
    typ: '',
    schwierigkeit: 'mittel',
    optionA: '',
    optionB: '',
    optionC: '',
    optionD: '',
    correctOption: '',
    yesnoAnswer: '',
    dragItem: '',
    questionImage: null
  });
  
  // View Management
  const [currentView, setCurrentView] = useState('waiting'); // 'waiting', 'video', 'quiz', 'success'
  
  // Student Navigation Control
  const [allowStudentNavigation, setAllowStudentNavigation] = useState(true);

  // Non-blocking confirmation modal (replaces window.confirm to avoid main-thread freeze)
  const [pendingConfirm, setPendingConfirm] = useState(null); // { type: 'stapel'|'frage', id, name }

  // Bestätigung für Stundenende (Pokal)
  const [showSuccessConfirm, setShowSuccessConfirm] = useState(false);

  // Hospitations-Icon
  const [observerState, dispatchObserver] = useReducer(observerReducer, initialObserverState);

  // ========== useEffect HOOKS (AFTER ALL STATE DECLARATIONS) ==========
  
  // ✅ Token-Authentifizierung
  React.useEffect(() => {
    // Lese Token aus der URL
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const roomLink = window.location.pathname.split('/mod/')[1] || window.location.pathname.split('/mod-view/')[1];
    
    if (!token) {
      setAuthError('Kein Authentifizierungs-Token vorhanden. Zugriff verweigert.');
      return;
    }

    setAuthToken(token);
    
    if (!roomLink) {
      setAuthError('Room-Link nicht gefunden.');
      return;
    }

    // Token validieren
    fetch('https://okadia.de/lr_token_validator.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        token: token,
        room_link: roomLink
      })
    })
    .then(res => res.json())
    .then(data => {
      if (data.success) {
        console.log('✅ Moderator-Token erfolgreich validiert:', data.user);
        setAuthUser(data.user);
        setAuthValidated(true);
      } else {
        setAuthError(data.message || 'Token-Validierung fehlgeschlagen');
      }
    })
    .catch((error) => {
      console.error('❌ Token-Validierungsfehler:', error);
      setAuthError('Fehler bei der Token-Validierung. Bitte versuchen Sie es erneut.');
    });
  }, []);

  // Überwache Schüleranzahl für Auto-Aktivierung der Navigation-Sperre
  useEffect(() => {
    const studentCount = Array.isArray(connectedStudents) ? connectedStudents.length : 0;
    
    // Auto-Aktivierung: Wenn ein zweiter Schüler beitritt, deaktiviere Schüler-Navigation
    if (studentCount >= 2 && allowStudentNavigation) {
      console.log(`🚫 Auto-Aktivierung Navigation-Sperre: ${studentCount} Schüler anwesend`);
      setAllowStudentNavigation(false);
      
      // Sende Update an alle Schüler
      if (socket.current?.connected) {
        socket.current.emit('navigationControlUpdate', {
          roomId: roomId,
          allowNavigation: false
        });
      }
    }
  }, [connectedStudents, allowStudentNavigation, roomId]);

  const handleCalculatorModeChange = (newMode) => {
    setCalculatorMode(newMode);
    if (socket.current?.connected) {
      socket.current.emit('calculatorModeUpdate', { roomId, mode: newMode });
    }
  };

  // ========== COMPUTED VALUES & FUNCTIONS ==========

  // Aktive Fragen (ohne deaktivierte) — selber Indexraum wie bei Schülern
  const activeQuestions = useMemo(() => questions.filter(q => !q.disabled), [questions]);
  const currentQuestion = activeQuestions[currentIndex];
  
  // View Management Function
  const handleViewChange = (newView) => {
    if (currentView === 'success') return; // Pokalseite kann nicht mehr verlassen werden
    if (newView === 'success') {
      setShowSuccessConfirm(true);
      return;
    }
    applyViewChange(newView);
  };

  const applyViewChange = (newView) => {
    console.log(`📺 Moderator changing view to: ${newView}`);
    setCurrentView(newView);
    if (socket.current?.connected) {
      socket.current.emit('moderatorSetView', { roomId: roomId, view: newView });
      if (newView === 'quiz') {
        socket.current.emit("startQuiz", { roomId });
      }
    } else {
      console.error('📺 Socket not connected!');
    }
  };

  const uploadWhiteboardImage = async (file) => {
    const dataBase64 = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result).split(',')[1]);
      r.onerror = reject;
      r.readAsDataURL(file);
    });
    const apiBase = process.env.REACT_APP_BACKEND_URL || 'https://rooms.okadia.de/api';
    const resp = await fetch(`${apiBase}/whiteboard/upload`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ mime: file.type, dataBase64 }),
    });
    if (!resp.ok) throw new Error('HTTP ' + resp.status);
    const json = await resp.json();
    const base = apiBase.replace(/\/api$/, '');
    return base + json.url;
  };

  const getFirstName = (name) => {
    if (!name) return null;
    return name.split(' ')[0];
  };
  
  // eslint-disable-next-line no-unused-vars
  const videoRef = useRef(null); // Wird für Schüler-Videos verwendet (falls später benötigt)

  // Hilfsfunktion: Finde alle Schüler, die eine bestimmte Antwort gewählt haben
  const getStudentsForAnswer = (answer) => {
    return Object.values(studentAnswers).filter(studentData => 
      studentData.answer === answer
    ).map(studentData => studentData.studentNumber);
  };

  // Hilfsfunktion: Finde alle Schüler, die noch nicht geantwortet haben
  const getStudentsWithoutAnswer = () => {
    const answeredStudents = Object.values(studentAnswers).map(data => data.studentNumber);
    return connectedStudents
      .map(student => student.studentNumber)
      .filter(studentNumber => !answeredStudents.includes(studentNumber));
  };

  // Hilfsfunktion: Erstelle Badge-Element für Schülernamen
  const renderStudentBadges = (studentNumbers) => {
    if (!studentNumbers || studentNumbers.length === 0) return null;
    
    return (
      <div className="student-badges">
        {studentNumbers.map(studentNumber => {
          // Finde den Schüler im connectedStudents Array
          const student = Array.isArray(connectedStudents) 
            ? connectedStudents.find(s => s && s.studentNumber === studentNumber)
            : null;
          
          const studentName = getFirstName(student?.name) || student?.name || `Schüler ${studentNumber}`;
          
          return (
            <span key={studentNumber} className="student-badge">
              {studentName}
            </span>
          );
        })}
      </div>
    );
  };

  // Stabiler Misch-Cache: Seed pro Frage-ID -> reproduzierbare Options-Reihenfolge
  const questionOrderCache = useRef({ seeds: new Map() });

  // Einzige Quelle für Mod-Anzeige UND Broadcast: mischt die Antwort-Optionen stabil und
  // hält Moderator und alle Schüler auf identischer Reihenfolge.
  const publishQuestions = (fullList) => {
    const stabilized = stabilizeQuestions(fullList, questionOrderCache.current);
    setQuestions(stabilized);
    const active = stabilized.filter((q) => !q.disabled);
    if (socket.current?.connected) {
      socket.current.emit('questionsListUpdated', { roomId, questions: active });
    }
    return stabilized;
  };

  // Fragen-Management Funktionen
  const loadAllQuestions = async () => {
    const apiBase = process.env.REACT_APP_BACKEND_URL || 'https://rooms.okadia.de/api';
    try {
      // First try with roomId for session-aware loading
      let response = await fetch(`${apiBase}/all-questions?roomId=${roomId}`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        console.log('Loading all questions without roomId...');
        // Fallback: Load all questions without roomId
        response = await fetch(`${apiBase}/all-questions`, {
          headers: getAuthHeaders()
        });
      }
      
      if (response.ok) {
        const data = await response.json();
        setAvailableQuestions(data);
        console.log('Alle Fragen geladen:', data.length);
      } else {
        console.error('Fehler beim Laden der Fragen:', response.status);
      }
    } catch (error) {
      console.error('Fehler beim Laden aller Fragen:', error);
    }
  };

  // Stapel laden
  const loadStapelListe = async () => {
    try {
      const response = await fetch('https://okadia.de/lr_stapel_api.php');
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setStapelListe(data.stapel || []);
        }
      }
    } catch (error) {
      console.error('Fehler beim Laden der Stapel:', error);
    }
  };

  // Einzelfragen laden
  const loadEinzelfragenListe = async () => {
    try {
      const response = await fetch('https://okadia.de/lr_einzelfragen_api.php');
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setEinzelfragenListe(data.fragen || []);
        }
      }
    } catch (error) {
      console.error('Fehler beim Laden der Einzelfragen:', error);
    }
  };

  // Stapel zur Session hinzufügen
  const addStapelToSession = async (stapelId, stapelName) => {
    const apiBase = process.env.REACT_APP_BACKEND_URL || 'https://rooms.okadia.de/api';
    try {
      const response = await fetch(`${apiBase}/add-stapel-to-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ 
          stapelId, 
          roomId
        }),
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log(`Stapel ${stapelName} zur Session hinzugefügt`);
        
        // Hole die Fragen des Stapels und füge sie zur Session hinzu
        if (result.fragen && result.fragen.length > 0) {
          // Lade aktualisierte Fragenliste vom Server (wie addQuestionToSession)
          const sessionResponse = await fetch(`${apiBase}/all-questions?roomId=${roomId}`, {
            headers: getAuthHeaders()
          });
          if (sessionResponse.ok) {
            const sessionData = await sessionResponse.json();
            publishQuestions(sessionData);
          }
        }

        setQuestionManagerView('main');
        return true;
      } else {
        const error = await response.json();
        console.error('Fehler beim Hinzufügen des Stapels:', error.error);
        return false;
      }
    } catch (error) {
      console.error('Fehler beim Hinzufügen des Stapels:', error);
      return false;
    }
  };

  // Neue Frage erstellen
  const createNewQuestion = async () => {
    const apiBase = process.env.REACT_APP_BACKEND_URL || 'https://rooms.okadia.de/api';
    try {
      const response = await fetch(`${apiBase}/create-question`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ 
          ...neueFrageForm,
          roomId
        }),
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('Neue Frage erstellt und hinzugefügt');
        
        // Frage zur lokalen Liste hinzufügen (Placeholder-Fragen herausfiltern)
        if (result.question) {
          const realQuestions = questions.filter(q => typeof q.id === 'number');
          const updatedQuestions = [...realQuestions, result.question];
          publishQuestions(updatedQuestions);
        }

        // Formular zurücksetzen
        setNeueFrageForm({
          text: '',
          typ: '',
          schwierigkeit: 'mittel',
          optionA: '',
          optionB: '',
          optionC: '',
          optionD: '',
          correctOption: '',
          yesnoAnswer: '',
          dragItem: '',
          questionImage: null
        });
        
        setQuestionManagerView('main');
        return true;
      } else {
        const error = await response.json();
        console.error('Fehler beim Erstellen der Frage:', error.error);
        return false;
      }
    } catch (error) {
      console.error('Fehler beim Erstellen der Frage:', error);
      return false;
    }
  };

  const addQuestionToSession = async (questionId) => {
    const apiBase = process.env.REACT_APP_BACKEND_URL || 'https://rooms.okadia.de/api';
    try {
      const response = await fetch(`${apiBase}/add-question-to-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ 
          questionId, 
          roomId,
          position: questions.length 
        }),
      });
      
      if (response.ok) {
        console.log(`Frage ${questionId} zur Session hinzugefügt`);
        
        // Lade die aktualisierten Session-Fragen vom Server
        const sessionResponse = await fetch(`${apiBase}/all-questions?roomId=${roomId}`, {
          headers: getAuthHeaders()
        });
        if (sessionResponse.ok) {
          const sessionData = await sessionResponse.json();
          publishQuestions(sessionData);
          console.log(`Session neu geladen: ${sessionData.length} Fragen total`);
        }
        
        // Auch verfügbare Fragen aktualisieren
        loadAllQuestions();
      } else {
        const error = await response.json();
        console.error('Fehler beim Hinzufügen der Frage:', error.error);
      }
    } catch (error) {
      console.error('Fehler beim Hinzufügen der Frage:', error);
    }
  };

  const removeQuestionFromSession = async (questionIndex) => {
    const apiBase = process.env.REACT_APP_BACKEND_URL || 'https://rooms.okadia.de/api';
    
    // Hole die questionId der zu entfernenden Frage
    const questionToRemove = questions[questionIndex];
    if (!questionToRemove) {
      console.error('Frage an Index nicht gefunden:', questionIndex);
      return;
    }

    // Synthetische Platzhalter-Fragen (z.B. info_1, error_1) existieren nicht in
    // room_sessions. Sie nur lokal aus der Liste entfernen, statt das Backend
    // aufzurufen (das sonst mit 404 "Frage in Session nicht gefunden" antwortet).
    const isRealQuestion = /^\d+$/.test(String(questionToRemove.id));
    if (!isRealQuestion) {
      const updatedQuestions = questions.filter((_, idx) => idx !== questionIndex);
      publishQuestions(updatedQuestions);
      return;
    }

    try {
      const response = await fetch(`${apiBase}/remove-question-from-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ 
          questionId: questionToRemove.id,
          roomId 
        }),
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('Frage deaktiviert:', result.message);
        
        // Markiere die Frage als deaktiviert statt sie zu entfernen
        const updatedQuestions = questions.map((q, index) =>
          index === questionIndex ? { ...q, disabled: true } : q
        );
        publishQuestions(updatedQuestions);

        // currentIndex ist im Raum der aktiven Fragen (wie bei Schülern).
        // Nach Entfernen: wenn Index nun außerhalb liegt, auf letztes Element klemmen.
        const newActiveCount = updatedQuestions.filter(q => !q.disabled).length;
        const clampedIndex = (currentIndex >= newActiveCount && newActiveCount > 0)
          ? newActiveCount - 1 : currentIndex;
        if (clampedIndex !== currentIndex) {
          setCurrentIndex(clampedIndex);
        }
        // Schüler autoritativ auf den (ggf. geänderten) aktuellen Index re-synchronisieren,
        // damit das Deaktivieren einer Frage keinen Lehrer/Schüler-Versatz erzeugt.
        if (socket.current && newActiveCount > 0) {
          socket.current.emit("moderatorSetQuestion", { roomId, questionIndex: clampedIndex });
        }
        
        // Auch verfügbare Fragen aktualisieren
        loadAllQuestions();
      } else {
        const error = await response.json();
        console.error('Fehler beim Deaktivieren der Frage:', error.error);
      }
    } catch (error) {
      console.error('Fehler beim Entfernen der Frage:', error);
    }
  };

  const reactivateQuestionInSession = async (questionId) => {
    const apiBase = process.env.REACT_APP_BACKEND_URL || 'https://rooms.okadia.de/api';
    try {
      const response = await fetch(`${apiBase}/reactivate-question-in-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ 
          questionId, 
          roomId 
        }),
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('Frage reaktiviert:', result.message);
        
        // Entferne disabled-Flag
        const updatedQuestions = questions.map(q =>
          q.id === questionId ? { ...q, disabled: false } : q
        );
        publishQuestions(updatedQuestions);
        
        loadAllQuestions();
        
        // Hole die reaktivierte Frage aus den verfügbaren Fragen und füge sie direkt hinzu
        const questionToReactivate = availableQuestions.find(q => q.id === questionId);
        if (questionToReactivate) {
          const updatedQuestions = [...questions, questionToReactivate];
          publishQuestions(updatedQuestions);
          console.log(`Session aktualisiert: ${updatedQuestions.length} Fragen total nach Reaktivierung`);
        }
        
        // Auch verfügbare Fragen aktualisieren
        loadAllQuestions();
      } else {
        const error = await response.json();
        console.error('Fehler beim Reaktivieren der Frage:', error.error);
      }
    } catch (error) {
      console.error('Fehler beim Reaktivieren der Frage:', error);
    }
  };

  const mvParse = (v, fb) => {
    if (v === null || v === undefined) return fb;
    if (typeof v === "string") { try { return JSON.parse(v); } catch (e) { return fb; } }
    return v;
  };
  // Funktion zum Rendern der Fragen wie bei Schülern, aber mit hervorgehobener richtiger Antwort
  const renderQuestionForModerator = () => {
    // Fallback für Video-Sessions ohne Quiz-Fragen
    if (!currentQuestion && questions.length === 0) {
      return (
        <div className="no-quiz-content">
          <h3>📺 Video-Session Modus</h3>
          <p>Diese Session hat keine Quiz-Fragen zugewiesen.</p>
          <p>Verwende den View-Slider um zwischen Video- und anderen Modi zu wechseln.</p>
        </div>
      );
    }
    
    if (!currentQuestion) return null;
    
    console.log('🎨 Rendering question for moderator:', currentQuestion.type);
    console.log('🎨 Student answers:', Object.keys(studentAnswers).length, 'students answered');
    
    switch (currentQuestion.type) {
      case "multipleChoice":
        return (
          <div className="quiz-container-with-badges">
            <div className="optionsGridStyle">
              {Object.entries(currentQuestion.options).map(([key, value]) => {
                const isCorrect = key === currentQuestion.correct;
                const studentsWhoChoseThis = getStudentsForAnswer(key);
                const isStudentAnswer = studentAnswer && studentAnswer.answer === key;

                let className = "buttonStyle";
                if (isCorrect) className += " button-correct moderator-correct-highlight";
                if (isStudentAnswer) {
                  className += studentAnswer.correct ? " student-answer-correct" : " student-answer-wrong";
                }

                return (
                  <div key={key} className="answer-option-with-badges">
                    <button
                      className={className}
                      disabled={true}
                    >
                      {key}) {value}
                      {isStudentAnswer && (
                        <span className="student-answer-indicator">
                          {studentAnswer.correct ? " ✓" : " ✗"}
                        </span>
                      )}
                    </button>
                    {renderStudentBadges(studentsWhoChoseThis)}
                  </div>
                );
              })}
            </div>

            {/* Wartezone für Schüler ohne Antwort */}
            <div className="waiting-zone">
              <h5>Warten auf Antwort:</h5>
              {renderStudentBadges(getStudentsWithoutAnswer())}
              {getStudentsWithoutAnswer().length === 0 && (
                <span className="no-waiting-students">Alle Schüler haben geantwortet</span>
              )}
            </div>
          </div>
        );
      case "imageQuestion":
        return (
          <div className="quiz-container-with-badges">
            {/* MODERATOR VERBESSERUNG 2: NUR kleinere Bilddarstellung mit Originalverhältnis */}
            {currentQuestion.image && (
              <div className="imageQuestionDisplayModerator">
                <img
                  src={currentQuestion.image}
                  alt="Question Image"
                  className="questionImageModerator"
                />
              </div>
            )}

            <div className="optionsGridStyle">
              {Object.entries(currentQuestion.options).map(([key, value]) => {
                const isCorrect = key === currentQuestion.correct;
                const studentsWhoChoseThis = getStudentsForAnswer(key);
                const isStudentAnswer = studentAnswer && studentAnswer.answer === key;

                let className = "buttonStyle";
                if (isCorrect) className += " button-correct moderator-correct-highlight";
                if (isStudentAnswer) {
                  className += studentAnswer.correct ? " student-answer-correct" : " student-answer-wrong";
                }

                return (
                  <div key={key} className="answer-option-with-badges">
                    <button
                      className={className}
                      disabled={true}
                    >
                      {key}) {value}
                      {isStudentAnswer && (
                        <span className="student-answer-indicator">
                          {studentAnswer.correct ? " ✓" : " ✗"}
                        </span>
                      )}
                    </button>
                    {renderStudentBadges(studentsWhoChoseThis)}
                  </div>
                );
              })}
            </div>

            {/* Wartezone für Schüler ohne Antwort */}
            <div className="waiting-zone">
              <h5>Warten auf Antwort:</h5>
              {renderStudentBadges(getStudentsWithoutAnswer())}
              {getStudentsWithoutAnswer().length === 0 && (
                <span className="no-waiting-students">Alle Schüler haben geantwortet</span>
              )}
            </div>
          </div>
        );
      case "yesno":
        return (
          <div className="quiz-container-with-badges">
            <div className="yesnoContainer">
              {["Ja", "Nein"].map((option) => {
                const isCorrect = option === currentQuestion.correct;
                const studentsWhoChoseThis = getStudentsForAnswer(option);
                const isStudentAnswer = studentAnswer && studentAnswer.answer === option;

                let className = `buttonStyle yesnoButton moderatorYesNoBtn`;
                if (isCorrect) className += " button-correct moderator-correct-highlight";
                if (isStudentAnswer) {
                  className += studentAnswer.correct ? " student-answer-correct" : " student-answer-wrong";
                }

                return (
                  <div key={option} className="answer-option-with-badges">
                    <button
                      className={className}
                      disabled={true}
                    >
                      {option}
                      {isStudentAnswer && (
                        <span className="student-answer-indicator">
                          {studentAnswer.correct ? " ✓" : " ✗"}
                        </span>
                      )}
                    </button>
                    {renderStudentBadges(studentsWhoChoseThis)}
                  </div>
                );
              })}
            </div>

            {/* Wartezone für Schüler ohne Antwort */}
            <div className="waiting-zone">
              <h5>Warten auf Antwort:</h5>
              {renderStudentBadges(getStudentsWithoutAnswer())}
              {getStudentsWithoutAnswer().length === 0 && (
                <span className="no-waiting-students">Alle Schüler haben geantwortet</span>
              )}
            </div>
          </div>
        );
      case "dragdrop":
        return (
          <div className="dragDropContainer">
            {/* MODERATOR VERBESSERUNG 3: Zeige das DragItem wie in App.js */}
            <div className="dragInstructions">
              <strong>Zu ziehende Element:</strong>
            </div>
            {currentQuestion.dragItem && (
              <div className="dragItemDisplay">
                <div className="dragItem moderatorDragItem">
                  {currentQuestion.dragItem}
                </div>
              </div>
            )}
            
            <div className="dropTargets">
              {currentQuestion.dropTargets?.map((target, index) => (
                <div
                  key={index}
                  className={`dropTarget ${target.correct ? "moderator-correct-highlight" : ""}`}
                >
                  {target.image ? (
                    <img src={target.image} alt="Drop Target" className="dragImage" />
                  ) : (
                    target.label || '[Drop Zone]'
                  )}
                </div>
              ))}
            </div>
            <div className="dragItemsContainer">
              {currentQuestion.pieces?.map((piece, index) => (
                <div key={index} className="dragItem">
                  {piece.image ? (
                    <img src={piece.image} alt="Drag Item" className="dragImage" />
                  ) : (
                    piece.label || piece.text || '[Drag Item]'
                  )}
                </div>
              ))}
            </div>
            {studentAnswer && (
              <div className={`student-drag-answer ${studentAnswer.correct ? "correct" : "wrong"}`}>
                {getFirstName(studentAnswer.studentName) || studentAnswer.studentName || `Schüler ${studentAnswer.studentNumber}`}: {studentAnswer.correct ? "Richtig zugeordnet ✓" : "Falsch zugeordnet ✗"}
              </div>
            )}
          </div>
        );
      case "multiDragdrop":
        return (
          <div className="multiDragDropContainer">
            <h4>Zuordnungs-Gruppen:</h4>
            <div className="multiDragGroupsContainer">
              {currentQuestion.pieces?.slice(0, 3).map((piece, pieceIndex) => {
                // Find the target with matching ID
                const correctTarget = currentQuestion.targets?.find(target => target.id === piece.id);
                
                return (
                  <div key={pieceIndex} className="multiDragGroup">
                    <div className="groupVerticalLayout">
                      <div className="groupPieces">
                        <div className="dragItem multiDragPiece">
                          {piece?.image ? (
                            <img src={piece.image} alt={piece.text || `Item ${pieceIndex + 1}`} className="dragImage itemImage" />
                          ) : (
                            piece?.label || piece?.text || `Item ${pieceIndex + 1}`
                          )}
                        </div>
                      </div>
                      <div className="groupConnector">↓</div>
                      <div className="groupTarget">
                        <div className="dropTarget moderator-correct-highlight">
                          {correctTarget?.image ? (
                            <img src={correctTarget.image} alt={correctTarget.label || correctTarget.text} className="dragImage targetImage" />
                          ) : (
                            correctTarget?.label || correctTarget?.text || `Ziel ${pieceIndex + 1}`
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {studentAnswer && (
              (!studentAnswer.correct && Array.isArray(studentAnswer.placements) && studentAnswer.placements.length > 0) ? (
                <div className="student-drag-answer wrong">
                  <button
                    type="button"
                    className="multiDetailToggle"
                    onClick={() => setMultiDetailOpen((o) => !o)}
                  >
                    <span className="multiDetailCaret">{multiDetailOpen ? "▾" : "▸"}</span>
                    {getFirstName(studentAnswer.studentName) || studentAnswer.studentName || `Schüler ${studentAnswer.studentNumber}`}: Falsch zugeordnet ✗
                  </button>
                  {multiDetailOpen && (
                    <ul className="multiDetailList">
                      {studentAnswer.placements.map((pl, i) => (
                        <li key={i} className={pl.correct ? "detail-correct" : "detail-wrong"}>
                          {pl.pieceLabel} → {pl.chosenTargetLabel} {pl.correct ? "✓" : `✗ (richtig: ${pl.correctTargetLabel})`}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : (
                <div className={`student-drag-answer ${studentAnswer.correct ? "correct" : "wrong"}`}>
                  {getFirstName(studentAnswer.studentName) || studentAnswer.studentName || `Schüler ${studentAnswer.studentNumber}`}: {studentAnswer.correct ? "Richtig zugeordnet ✓" : "Falsch zugeordnet ✗"}
                </div>
              )
            )}
          </div>
        );
      case "matchpairs":
        return (
          <div className="matchPairsContainer">
            {/* MODERATOR VERBESSERUNG 4: Drei zusammengehörige Paare untereinander mit Trennstrichen */}
            <div className="matchPairsDisplay">
              <h4>Zuordnungs-Paare:</h4>
              <div className="pairsGrid">
                {currentQuestion.pairs && currentQuestion.pairs.map((pair, index) => (
                  <div key={index} className="pairGroup">
                    <div className="pairItem left-item">
                      <span className="pair-label">Links:</span>
                      <div className="pair-content">{pair.left}</div>
                    </div>
                    <div className="pairConnector">↔</div>
                    <div className="pairItem right-item">
                      <span className="pair-label">Rechts:</span>
                      <div className="pair-content">{pair.right}</div>
                    </div>
                    <div className="pairItem match-item">
                      <span className="pair-label">Zuordnung:</span>
                      <div className="pair-content correctHighlight">{pair.correct_match}</div>
                    </div>
                    {index < currentQuestion.pairs.length - 1 && (
                      <div className="pairSeparator">━━━━━━━━━━━━━━━━━━━━</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            
            {studentAnswer && (
              <div className={`student-match-answer ${studentAnswer.correct ? "correct" : "wrong"}`}>
                {getFirstName(studentAnswer.studentName) || studentAnswer.studentName || `Schüler ${studentAnswer.studentNumber}`}: {studentAnswer.correct ? "Richtig zugeordnet ✓" : "Falsch zugeordnet ✗"}
              </div>
            )}
            
            <div className="correctAnswerModerator">
              <p>Alle richtigen Zuordnungen sind oben grün markiert</p>
            </div>
          </div>
        );
      case "lueckentext_ausfuellen": {
        const gaps = mvParse(currentQuestion.gaps, []);
        const parts = String(currentQuestion.question || "").split(/\{\{(\d+)\}\}/);
        return (
          <div className="quiz-container-with-badges">
            <p className="lueckentextSatz">
              {parts.map((part, i) => {
                if (i % 2 === 1) {
                  const gi = parseInt(part, 10) - 1;
                  const alts = Array.isArray(gaps[gi]) ? gaps[gi] : (gaps[gi] != null ? [gaps[gi]] : []);
                  return <span key={i} className="lueckeLoesung moderator-correct-highlight">{alts.join(" / ")}</span>;
                }
                return <span key={i}>{part}</span>;
              })}
            </p>
            <div className="waiting-zone">
              <h5>Warten auf Antwort:</h5>
              {renderStudentBadges(getStudentsWithoutAnswer())}
              {getStudentsWithoutAnswer().length === 0 && (<span className="no-waiting-students">Alle Schüler haben geantwortet</span>)}
            </div>
          </div>
        );
      }
      case "lueckentext_zuordnung": {
        const gaps = mvParse(currentQuestion.gaps, []);
        const bank = mvParse(currentQuestion.words, []);
        const parts = String(currentQuestion.question || "").split(/\{\{(\d+)\}\}/);
        return (
          <div className="quiz-container-with-badges">
            <div className="zuordnungBank">{bank.map((w, i) => <span key={i} className="zuordnungChip">{w}</span>)}</div>
            <p className="lueckentextSatz">
              {parts.map((part, i) => {
                if (i % 2 === 1) {
                  const gi = parseInt(part, 10) - 1;
                  const sol = gaps[gi];
                  return <span key={i} className="lueckeLoesung moderator-correct-highlight">{Array.isArray(sol) ? sol.join(" / ") : sol}</span>;
                }
                return <span key={i}>{part}</span>;
              })}
            </p>
            <div className="waiting-zone">
              <h5>Warten auf Antwort:</h5>
              {renderStudentBadges(getStudentsWithoutAnswer())}
              {getStudentsWithoutAnswer().length === 0 && (<span className="no-waiting-students">Alle Schüler haben geantwortet</span>)}
            </div>
          </div>
        );
      }
      case "satzpuzzle": {
        const order = mvParse(currentQuestion.words, []);
        return (
          <div className="quiz-container-with-badges">
            <div className="satzpuzzleAnswerRow">
              {order.map((w, i) => <span key={i} className="satzpuzzleTile chosen moderator-correct-highlight">{w}</span>)}
            </div>
            <div className="waiting-zone">
              <h5>Warten auf Antwort:</h5>
              {renderStudentBadges(getStudentsWithoutAnswer())}
              {getStudentsWithoutAnswer().length === 0 && (<span className="no-waiting-students">Alle Schüler haben geantwortet</span>)}
            </div>
          </div>
        );
      }
      case "wortmarkierung": {
        const words = String(currentQuestion.question || "").split(/\s+/).filter(Boolean);
        const ci = parseInt(currentQuestion.correct, 10);
        return (
          <div className="quiz-container-with-badges">
            <div className="wortmarkierungContainer">
              {words.map((w, i) => <span key={i} className={"wortToken" + (i === ci ? " button-correct moderator-correct-highlight" : "")}>{w}</span>)}
            </div>
            <div className="waiting-zone">
              <h5>Warten auf Antwort:</h5>
              {renderStudentBadges(getStudentsWithoutAnswer())}
              {getStudentsWithoutAnswer().length === 0 && (<span className="no-waiting-students">Alle Schüler haben geantwortet</span>)}
            </div>
          </div>
        );
      }
      case "multi_multiplechoice": {
        const opts = mvParse(currentQuestion.options, {});
        const correctKeys = mvParse(currentQuestion.words, []).map(String);
        return (
          <div className="quiz-container-with-badges">
            <div className="optionsGridStyle">
              {Object.entries(opts).map(([key, value]) => {
                const isC = correctKeys.includes(String(key));
                return (
                  <button key={key} className={"buttonStyle" + (isC ? " button-correct moderator-correct-highlight" : "")} disabled={true}>
                    {isC ? "☑" : "☐"} {key}) {value}
                  </button>
                );
              })}
            </div>
            <div className="waiting-zone">
              <h5>Warten auf Antwort:</h5>
              {renderStudentBadges(getStudentsWithoutAnswer())}
              {getStudentsWithoutAnswer().length === 0 && (<span className="no-waiting-students">Alle Schüler haben geantwortet</span>)}
            </div>
          </div>
        );
      }
      case "kreuzwortraetsel": {
        const entries = mvParse(currentQuestion.grid, []);
        const cells = {}; let maxR = 0, maxC = 0;
        entries.forEach((e) => {
          const ans = String(e.answer || "");
          for (let k = 0; k < ans.length; k++) {
            const r = e.dir === "down" ? e.row + k : e.row;
            const c = e.dir === "down" ? e.col : e.col + k;
            cells[`${r},${c}`] = ans[k];
            maxR = Math.max(maxR, r); maxC = Math.max(maxC, c);
          }
        });
        const rows = [];
        for (let r = 0; r <= maxR; r++) {
          const tds = [];
          for (let c = 0; c <= maxC; c++) {
            const kk = `${r},${c}`;
            tds.push(cells[kk]
              ? <td key={c} className="cwCell cwCellSolved moderator-correct-highlight">{cells[kk]}</td>
              : <td key={c} className="cwCellEmpty"></td>);
          }
          rows.push(<tr key={r}>{tds}</tr>);
        }
        return (
          <div className="quiz-container-with-badges">
            <table className="cwTable"><tbody>{rows}</tbody></table>
            <div className="cwClues">{entries.map((e, i) => <div key={i} className="cwClue">{e.dir === "down" ? "↓" : "→"} {e.clue} = <strong>{e.answer}</strong></div>)}</div>
            <div className="waiting-zone">
              <h5>Warten auf Antwort:</h5>
              {renderStudentBadges(getStudentsWithoutAnswer())}
              {getStudentsWithoutAnswer().length === 0 && (<span className="no-waiting-students">Alle Schüler haben geantwortet</span>)}
            </div>
          </div>
        );
      }
      default:
        return <p>Unbekannter Fragetyp: {currentQuestion.type}</p>;
    }
  };

  // Vorschau-Rendering für den Fragen-Browser (ohne Schülerantworten)
  const renderBrowserQuestionPreview = (q) => {
    if (!q) return null;
    switch (q.type) {
      case "multipleChoice":
      case "imageQuestion":
        return (
          <div>
            {q.image && <img src={q.image} alt="Frage" style={{ maxWidth: '100%', borderRadius: '8px', marginBottom: '12px' }} />}
            <div className="optionsGridStyle">
              {Object.entries(q.options || {}).map(([key, value]) => (
                <button
                  key={key}
                  className={`buttonStyle ${key === q.correct ? 'button-correct moderator-correct-highlight' : ''}`}
                  disabled
                >
                  {key}) {value}
                </button>
              ))}
            </div>
          </div>
        );
      case "yesno":
        return (
          <div>
            <div className="yesnoContainer">
              {["Ja", "Nein"].map(option => (
                <button
                  key={option}
                  className={`buttonStyle yesnoButton ${option === q.correct ? 'button-correct moderator-correct-highlight' : ''}`}
                  disabled
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        );
      case "dragdrop":
        return (
          <div>
            <p><strong>Zu ziehend:</strong> {q.dragItem}</p>
            <div className="dropTargets">
              {q.dropTargets?.map((t, i) => (
                <div key={i} className={`dropTarget ${t.correct ? 'moderator-correct-highlight' : ''}`}>
                  {t.label || '[Zone]'}
                </div>
              ))}
            </div>
          </div>
        );
      case "matchpairs":
        return (
          <div>
            {q.pairs?.map((pair, i) => (
              <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px', fontSize: '14px' }}>
                <span style={{ background: '#e3f2fd', padding: '4px 8px', borderRadius: '4px' }}>{pair.left}</span>
                <span>↔</span>
                <span style={{ background: '#e8f5e9', padding: '4px 8px', borderRadius: '4px' }}>{pair.right}</span>
                <span style={{ color: '#555', fontSize: '12px' }}>→ {pair.correct_match}</span>
              </div>
            ))}
          </div>
        );
      case "lueckentext_ausfuellen": {
        const gaps = mvParse(q.gaps, []);
        const parts = String(q.question || "").split(/\{\{(\d+)\}\}/);
        return (
          <p className="lueckentextSatz">
            {parts.map((part, i) => i % 2 === 1
              ? <span key={i} className="lueckeLoesung moderator-correct-highlight">{(Array.isArray(gaps[parseInt(part,10)-1]) ? gaps[parseInt(part,10)-1] : [gaps[parseInt(part,10)-1]]).join(" / ")}</span>
              : <span key={i}>{part}</span>)}
          </p>
        );
      }
      case "lueckentext_zuordnung": {
        const gaps = mvParse(q.gaps, []);
        const bank = mvParse(q.words, []);
        const parts = String(q.question || "").split(/\{\{(\d+)\}\}/);
        return (
          <div>
            <div className="zuordnungBank">{bank.map((w, i) => <span key={i} className="zuordnungChip">{w}</span>)}</div>
            <p className="lueckentextSatz">
              {parts.map((part, i) => i % 2 === 1
                ? <span key={i} className="lueckeLoesung moderator-correct-highlight">{(() => { const sol = gaps[parseInt(part,10)-1]; return Array.isArray(sol) ? sol.join(" / ") : sol; })()}</span>
                : <span key={i}>{part}</span>)}
            </p>
          </div>
        );
      }
      case "satzpuzzle": {
        const order = mvParse(q.words, []);
        return <div className="satzpuzzleAnswerRow">{order.map((w, i) => <span key={i} className="satzpuzzleTile chosen moderator-correct-highlight">{w}</span>)}</div>;
      }
      case "wortmarkierung": {
        const words = String(q.question || "").split(/\s+/).filter(Boolean);
        const ci = parseInt(q.correct, 10);
        return <div className="wortmarkierungContainer">{words.map((w, i) => <span key={i} className={"wortToken" + (i === ci ? " button-correct moderator-correct-highlight" : "")}>{w}</span>)}</div>;
      }
      case "multi_multiplechoice": {
        const opts = mvParse(q.options, {});
        const correctKeys = mvParse(q.words, []).map(String);
        return (
          <div className="optionsGridStyle">
            {Object.entries(opts).map(([key, value]) => (
              <button key={key} className={"buttonStyle" + (correctKeys.includes(String(key)) ? " button-correct moderator-correct-highlight" : "")} disabled={true}>
                {correctKeys.includes(String(key)) ? "☑" : "☐"} {key}) {value}
              </button>
            ))}
          </div>
        );
      }
      case "kreuzwortraetsel": {
        const entries = mvParse(q.grid, []);
        return (
          <div className="cwClues">
            {entries.map((e, i) => <div key={i} className="cwClue">{e.dir === "down" ? "↓" : "→"} {e.clue} = <strong>{e.answer}</strong></div>)}
          </div>
        );
      }
      default:
        return <p>Typ: {q.type}</p>;
    }
  };

useEffect(() => {
  if (!authValidated || !authToken) return;

  // Use Environment Variable for Socket.IO URL
  const socketUrl = process.env.REACT_APP_SOCKET_URL || 'https://rooms.okadia.de';
  console.log('🌐 Moderator Socket connection to:', socketUrl);
  console.log('🌍 Environment REACT_APP_SOCKET_URL:', process.env.REACT_APP_SOCKET_URL);
  console.log('🏠 Room ID from params:', roomId);
  
  socket.current = io(socketUrl, {
    transports: ['websocket', 'polling'],
    upgrade: true,
    timeout: 20000,
    auth: {
      token: authToken,
      roomId: roomId,
      role: "moderator"
    }
  });

    socket.current.on('connect', () => {
      console.log('Verbunden mit dem Socket-Server');
      console.log("📡 Moderator joining room:", roomId);
      
      // Join the correct room from URL parameter
      socket.current.emit("joinRoom", { role: "mod", roomId: roomId });

      // Falls Fragen schon geladen wurden, bevor der Socket verbunden war:
      // jetzt die massgebliche Fragenliste an die Schueler senden. WICHTIG: exakt
      // derselbe Filter wie publishQuestions/activeQuestions (nur !disabled) – sonst
      // bekämen Schüler ein abweichendes Subset und dieselbe Index-Position würde eine
      // andere Frage (mit anderen Antwortmöglichkeiten) zeigen als beim Moderator.
      const realActive = (questionsRef.current || []).filter((q) => !q.disabled);
      if (realActive.length > 0) {
        socket.current.emit('questionsListUpdated', { roomId, questions: realActive });
      }
      
      // ZUSÄTZLICH: Lausche auf alle Events für Debug
      console.log("🎧 Setting up event listeners...");
    });

    socket.current.on('connect_error', (error) => {
      console.error('Socket.IO connection error:', error);
    });

    socket.current.on('questionChanged', (data) => {
      const parsedIndex = parseInt(data.questionIndex, 10);
      if (!isNaN(parsedIndex)) {
        // Moderator folgt der (freien) Schülernavigation. Kein Clamp gegen
        // activeQuestions.length: die Länge stünde hier nur aus einer veralteten
        // Closure zur Verfügung und würde den Moderator fälschlich auf eine frühe
        // Frage klemmen. Der Schüler meldet ohnehin nur gültige Indizes.
        setCurrentIndex(parsedIndex);

        // Reset student answers when question changes
        setStudentAnswers({});
        setStudentAnswer(null);
        setMultiDetailOpen(false);
      }
    });

    // Autoritative Fragensynchronisation (Lehrer-Navigation via moderatorSetQuestion,
    // Frage-Deaktivieren-Resync): der Server sendet syncQuestion an ALLE im Raum – also
    // auch an den Moderator. Da der Schüler auf syncQuestion NICHT mehr zurück-echot,
    // muss der Moderator die Schülerantwort-Anzeige hier zurücksetzen (früher geschah
    // das über den questionChanged-Echo des Schülers).
    socket.current.on('syncQuestion', (data) => {
      const idx = parseInt(data?.questionIndex, 10);
      if (!isNaN(idx)) {
        setCurrentIndex(idx);
        setStudentAnswers({});
        setStudentAnswer(null);
        setMultiDetailOpen(false);
      }
    });

    // Nav-Status vom Server übernehmen (wird beim Join gesendet). Ohne diesen Listener
    // zeigte der Lehrer nach dem Betreten eines gesperrten Raums fälschlich "erlaubt",
    // während die Schüler gesperrt waren. Kein Rück-Emit (der Server schickt das nur an
    // den Moderator, nie im Kreis).
    socket.current.on('navigationControlUpdate', (data) => {
      if (typeof data?.allowNavigation === 'boolean') {
        setAllowStudentNavigation(data.allowNavigation);
      }
    });

    // === NEW: Listen for view changes (e.g., from server on reconnect) ===
    socket.current.on('viewChanged', (data) => {
      console.log('📺 ModView received view change:', data.view);
      console.log('📺 Setting currentView from:', currentView, 'to:', data.view);
      setCurrentView(data.view);
    });

    socket.current.on('studentAnswer', (data) => {
      console.log('🎯 Student answer received in ModView:', data);
      console.log(`📝 Student ${data.studentNumber} antwortete:`, data.answer);
      console.log('🔍 Full answer data:', {
        studentId: data.studentId,
        studentNumber: data.studentNumber,
        answer: data.answer,
        correct: data.correct,
        roomId: data.roomId,
        questionType: data.questionType
      });
      
      // Multi-User: Speichere Antwort mit Schüler-ID
      setStudentAnswers(prev => ({
        ...prev,
        [data.studentId]: {
          answer: data.answer,
          correct: data.correct,
          studentNumber: data.studentNumber,
          timestamp: data.timestamp,
          questionType: data.questionType,
          placements: data.placements
        }
      }));
      
      console.log('📊 Updated studentAnswers state');

      // Legacy support: Setze auch studentAnswer für Rückwärtskompatibilität
      setStudentAnswer(data);
      setMultiDetailOpen(false); // neues Antwort-Event -> Detail-Panel einklappen, damit keine fremden Daten unter offenem Caret erscheinen

      // Per-Schüler Punktestand aktualisieren
      setStudentScores(prev => ({
        ...prev,
        [data.studentNumber]: {
          correct: (prev[data.studentNumber]?.correct || 0) + (data.correct ? 1 : 0),
          wrong: (prev[data.studentNumber]?.wrong || 0) + (data.correct ? 0 : 1),
        }
      }));

      // Update correct/wrong counts
      if (data.correct) {
        setCorrectCount(prev => prev + 1);
      } else {
        setWrongCount(prev => prev + 1);
      }
    });
    
    // Multi-User Events
    socket.current.on('studentsUpdate', (data) => {
      console.log('👥 Students update received:', data);
      setConnectedStudents(prev => {
        const prevStudents = Array.isArray(prev) ? prev : [];
        return data.students.map(newStudent => {
          const existingStudent = prevStudents.find(s =>
            s && s.studentNumber === newStudent.studentNumber
          );
          return {
            ...newStudent,
            name: newStudent.name || existingStudent?.name,
            socketId: existingStudent?.socketId || newStudent.socketId || newStudent.id,
            hasVideo: existingStudent?.hasVideo || false
          };
        });
      });
      setRoomCapacity(data.roomCapacity || `${data.totalStudents}/4`);
    });
    
    socket.current.on('roomUpdate', (data) => {
      console.log('🔄 Room update received:', data);
      
      // KRITISCH: Erhalte bestehende hasVideo-Status beim Update
      setConnectedStudents(prev => {
        const prevStudents = Array.isArray(prev) ? prev : [];

        return data.students.map(newStudent => {
          const existingStudent = prevStudents.find(s =>
            s && s.studentNumber === newStudent.studentNumber
          );

          return {
            ...newStudent,
            name: newStudent.name || existingStudent?.name,
            socketId: existingStudent?.socketId || newStudent.socketId || newStudent.id,
            hasVideo: existingStudent?.hasVideo || false
          };
        });
      });
      
      setRoomCapacity(`${data.totalStudents}/4`);
      
      if (data.studentLeft) {
        console.log(`👋 ${data.studentLeft} hat den Raum verlassen`);
      }
    });
    
    socket.current.on('testEvent', (data) => {
      console.log('🧪 TEST EVENT received:', data);
    });

    socket.current.on('progressUpdate', (data) => {
      setCorrectCount(prev => prev + (data.correct || 0));
      setWrongCount(prev => prev + (data.wrong || 0));
    });

    return () => {
      socket.current.disconnect();
    };
  }, [roomId, authValidated, authToken]); // FIXED: Add authValidated and authToken to dependencies

  // Haelt questionsRef synchron mit dem questions-State (fuer Re-Emit im connect-Handler)
  useEffect(() => { questionsRef.current = questions; }, [questions]);

  useEffect(() => {
    // Prüfe zuerst, ob es sich um einen Quiz-Termin handelt
    const checkAndLoadQuestions = async () => {
      if (!roomId) {
        console.error('Keine Room-ID verfügbar');
        return;
      }
      
      try {
        // Schritt 1: Prüfe Session-Mapping
        console.log(`🔍 [Moderator] Prüfe Session-Typ für: ${roomId}`);
        const mappingResponse = await fetch(`https://okadia.de/lr_session_mapping_simple.php?room_id=${encodeURIComponent(roomId)}`);
        
        if (!mappingResponse.ok) {
          throw new Error(`Mapping API error! status: ${mappingResponse.status}`);
        }
        
        const mappingData = await mappingResponse.json();
        
        if (!mappingData.success) {
          throw new Error(mappingData.message || 'Session-Mapping fehlgeschlagen');
        }
        
        // Schritt 2: Wenn es keine Quiz-Session ist, keine Fragen laden (aber View-Kontrolle behalten)
        if (!mappingData.is_quiz_session) {
          console.log(`� [Moderator] Video-Session erkannt: ${roomId} - keine Quiz-Fragen`);
          setQuestions([]); // Leere Fragen-Array für Video-Sessions
          // WICHTIG: Für Moderator NICHT den View automatisch ändern - Slider-Kontrolle behalten
          console.log(`🎛️ [Moderator] Behalte View-Kontrolle für Video-Session`);
          return;
        }
        
        // Sendet die massgebliche (aktive) Fragenliste an alle Schueler.
        const apiBase = process.env.REACT_APP_BACKEND_URL || 'https://rooms.okadia.de/api';

        // Schritt 3a: Quelle der Wahrheit ist room_sessions. Zuerst die echten
        // Session-Fragen laden; nur wenn die Session leer ist, aus der
        // TKurs-Zuordnung (PHP-API) seeden.
        try {
          const sessionResponse = await fetch(`${apiBase}/all-questions?roomId=${encodeURIComponent(roomId)}`, {
            headers: getAuthHeaders()
          });
          if (sessionResponse.ok) {
            const sessionQuestions = await sessionResponse.json();
            if (Array.isArray(sessionQuestions) && sessionQuestions.length > 0) {
              console.log(`✅ [Moderator] ${sessionQuestions.length} Session-Fragen aus room_sessions geladen`);
              publishQuestions(sessionQuestions);
              return;
            }
          }
        } catch (sessionError) {
          console.error('Fehler beim Laden der Session-Fragen:', sessionError);
        }

        // Schritt 3b: Session leer/uninitialisiert - aus TKursNr seeden
        const tkursNr = mappingData.tkurs_nr;
        console.log(`📚 [Moderator] Leere Session - lade TKurs-Fragen für TKursNr: ${tkursNr}`);
        
        const questionsResponse = await fetch(`https://okadia.de/lr_questions_api.php?tkurs_nr=${encodeURIComponent(tkursNr)}`);
        
        if (!questionsResponse.ok) {
          throw new Error(`Quiz API error! status: ${questionsResponse.status}`);
        }
        
        const questionsData = await questionsResponse.json();
        
        if (questionsData.success && questionsData.questions && questionsData.questions.length > 0) {
          console.log(`✅ ${questionsData.questions.length} Quiz-Fragen für TKursNr ${tkursNr} geladen`);
          
          // WICHTIG: Session mit den geladenen Fragen initialisieren
          try {
            const initResponse = await fetch(`${apiBase}/initialize-session`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
              body: JSON.stringify({ 
                roomId: roomId,
                questions: questionsData.questions 
              }),
            });
            
            if (initResponse.ok) {
              const initResult = await initResponse.json();
              console.log(`🔄 Session-Initialisierung: ${initResult.message}`);
            } else {
              console.warn('Session-Initialisierung fehlgeschlagen, aber Fragen werden trotzdem geladen');
            }
          } catch (initError) {
            console.error('Fehler bei Session-Initialisierung:', initError);
          }
          
          publishQuestions(questionsData.questions);
        } else {
          console.warn(`⚠️ Keine Fragen für TKursNr ${tkursNr} ausgewählt:`, questionsData.message);
          setQuestions([]);
        }
      } catch (error) {
        console.error('❌ Fehler beim Laden der Session-Daten:', error);
        setQuestions([]);
      }
    };
    
    checkAndLoadQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, authValidated, authToken]);

  // ========== Move startStream to top-level function inside ModView ==========
  // Startet den Mod-Stream (Producer)
  async function startStream() {
    const device = new (await import("mediasoup-client")).Device();
    const rtpCapabilities = await new Promise((resolve) => {
      mediasoupSocket.current.emit("getRtpCapabilities", {}, (data) => {
        resolve(data.rtpCapabilities);
      });
    });
    await device.load({ routerRtpCapabilities: rtpCapabilities });

    const { params: transportOptions } = await new Promise((resolve) => {
      mediasoupSocket.current.emit("createWebRtcTransport", { direction: "send" }, (data) => {
        resolve(data);
      });
    });

    const iceServers = await getIceServers(authToken);
    const sendTransport = device.createSendTransport({ ...transportOptions, iceServers });

    sendTransport.on("connect", ({ dtlsParameters }, callback, errback) => {
      mediasoupSocket.current.emit("connectTransport", {
        transportId: sendTransport.id,
        dtlsParameters,
        direction: "send",
      }, (response) => {
        if (response === 'connected') { callback(); }
        else { errback(new Error('connectTransport failed')); }
      });
    });

    sendTransport.on("produce", ({ kind, rtpParameters }, callback, errback) => {
      mediasoupSocket.current.emit("produce", {
        transportId: sendTransport.id,
        kind,
        rtpParameters,
      }, ({ id }) => callback({ id }));
    });

    const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
    // Persistenter Speicher für Recovery-Interval
    window.modCameraStream = stream;

    // Kamera-Berechtigung erteilt = User-Gesture vorhanden.
    // Schüler-Audio, das vorher durch die Autoplay-Policy blockiert wurde (audio.play()
    // schlägt fehl wenn noch keine Nutzerinteraktion auf der Seite stattgefunden hat),
    // jetzt erneut starten.
    Object.values(studentAudioElements.current).forEach(audioEl => {
      if (audioEl && audioEl.paused && audioEl.srcObject) {
        console.log('🔄 [startStream] Retry blockiertes Student-Audio nach User-Gesture');
        audioEl.play().catch(e => console.error('❌ Audio retry error:', e));
      }
    });

    // Bind stream to moderatorVideoRef instead of creating floating element
    if (moderatorVideoRef.current) {
      moderatorVideoRef.current.srcObject = stream;
      moderatorVideoRef.current.muted = true;  // Verhindere Audio-Feedback
      moderatorVideoRef.current.playsInline = true;
      console.log("✅ Moderator-Stream an moderatorVideoRef gebunden (muted)");
    }

    for (const track of stream.getTracks()) {
      await sendTransport.produce({ track });
    }

    console.log("🎬 Mod-Stream wurde produziert");
    mediasoupSocket.current.emit("modStream"); // signalisiert Schüler-Client den Streamstart
    setStreamStarted(true); // Mute-Buttons aktivieren
    emitMediaState(videoMuted, audioMuted); // aktuellen Mute-Status an Schüler synchronisieren
  }

  // Sendet den aktuellen Audio/Video-Mute-Status an den Raum (Schüler blenden Kurt ein).
  function emitMediaState(nextVideoMuted, nextAudioMuted) {
    if (socket.current) {
      socket.current.emit('modMediaState', {
        roomId,
        videoMuted: nextVideoMuted,
        audioMuted: nextAudioMuted,
      });
    }
  }

  // Eigenes Mikrofon stummschalten/aktivieren (track.enabled → Schüler hören Stille).
  function toggleAudioMute() {
    const stream = window.modCameraStream;
    const track = stream && stream.getAudioTracks()[0];
    if (!track) return;
    const nextMuted = !audioMuted;
    track.enabled = !nextMuted;
    setAudioMuted(nextMuted);
    emitMediaState(videoMuted, nextMuted);
  }

  // Eigene Kamera aus-/einschalten (track.enabled=false → Schüler sehen Kurt statt Schwarzbild).
  function toggleVideoMute() {
    const stream = window.modCameraStream;
    const track = stream && stream.getVideoTracks()[0];
    if (!track) return;
    const nextMuted = !videoMuted;
    track.enabled = !nextMuted;
    setVideoMuted(nextMuted);
    emitMediaState(nextMuted, audioMuted);
  }

  useEffect(() => {
    if (!authToken || !authValidated) return;
    // MediaSoup Socket.IO Connection - use base URL with path option
    const mediasoupBaseUrl = 'https://rooms.okadia.de';
    const mediasoupPath = '/mediasoup/socket.io/';
    console.log('Connecting to MediaSoup server:', mediasoupBaseUrl, 'with path:', mediasoupPath);

    mediasoupSocket.current = io(mediasoupBaseUrl, {
      path: mediasoupPath,
      transports: ['websocket', 'polling'],
      auth: { token: authToken, role: "mod" },
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 10000
    });
    
    // DEDUPLICATION: Tracke bereits verarbeitete Student-Streams mit STATE
    const processedStreams = new Map(); // streamKey -> { loading: boolean, trackCount: number, timestamp: number }

    mediasoupSocket.current.on('connect', () => {
      console.log('✅ Connected to MediaSoup server');
      // Join MediaSoup with correct room from URL parameter
      console.log("📡 MediaSoup joining room:", roomId);
      mediasoupSocket.current.emit("joinRoom", { roomId: roomId, role: "mod" });
      // Aktiv alle bereits vorhandenen Streams anfordern (unabhängig vom Event-Timing).
      mediasoupSocket.current.emit("requestStreams");
      
      // IMPORTANT: Nur bei ECHTEM Reconnect leeren, nicht bei normaler Verbindung
      // Das Connect-Event wird auch bei normalen Verbindungen ausgelöst
      console.log('✅ MediaSoup verbunden - Video-Elemente bleiben erhalten');
    });
    
    mediasoupSocket.current.on('connect_error', (error) => {
      console.error('❌ MediaSoup connection error:', error);
    });
    
    mediasoupSocket.current.on('disconnect', () => {
      console.warn('⚠️ MediaSoup disconnected, trying to reconnect...');
    });
    
    mediasoupSocket.current.on('reconnect', (attemptNumber) => {
      console.log(`🔄 MediaSoup reconnected after ${attemptNumber} attempts`);

      // NUR bei echtem Reconnect alle Video-Elemente leeren
      for (let i = 0; i < 4; i++) {
        const videoEl = studentVideoRefs.current[i];
        if (videoEl && videoEl.current) {
          videoEl.current.srcObject = null;
          delete videoEl.current.dataset.studentNumber;
        }
      }
      processedStreams.clear();
      console.log('🔄 Video-Elemente geleert nach ECHTEM Reconnect');

      // Device/Transport-Zustand verwerfen — die Server-Closure ist nach Reconnect leer,
      // der alte recvTransport zeigt ins Leere (Ursache C).
      try { if (globalRecvTransport) globalRecvTransport.close(); } catch (e) { /* ignore */ }
      globalRecvTransport = null;
      globalDevice = null;
      isDeviceInitialized = false;
      initDevicePromise = null;

      console.log('🔄 Device/Transport nach Reconnect verworfen — wird bei nächstem Stream neu aufgebaut');
      // joinRoom + requestStreams erneut anstoßen (Server kennt den neuen Socket noch nicht)
      mediasoupSocket.current.emit("joinRoom", { roomId: roomId, role: "mod" });
      mediasoupSocket.current.emit("requestStreams");
    });

    // GLOBALES DEVICE UND TRANSPORT für alle Student-Streams
    let globalDevice = null;
    let globalRecvTransport = null;
    let isDeviceInitialized = false;
    let initDevicePromise = null; // Promise-Guard gegen Race Condition bei parallelen Aufrufen
    // Serialisiert die SDP-Verhandlung auf dem geteilten globalRecvTransport: Treten zwei
    // Schueler nahezu gleichzeitig bei, wuerden zwei parallele transport.consume()-Aufrufe
    // auf demselben Transport in Safari die laufende SDP-Verhandlung gegenseitig korrumpieren
    // (Video faellt lautlos aus, Audio bleibt meist unbeeinflusst). Jeder Aufruf wartet daher
    // auf die Kette der vorherigen, bevor er selbst verhandelt.
    let consumeMutex = Promise.resolve();

    // ZENTRALE FUNCTION: Initialisiere Device nur einmal
    // Promise-Guard stellt sicher, dass bei gleichzeitigen Aufrufen nur EINE Initialisierung
    // durchgeführt wird und beide auf dasselbe Ergebnis warten.
    const initializeDevice = async () => {
      if (isDeviceInitialized) return { device: globalDevice, transport: globalRecvTransport };
      if (initDevicePromise) return await initDevicePromise;
      
      initDevicePromise = (async () => {
      try {
        const mediasoupClient = await import("mediasoup-client");
        globalDevice = new mediasoupClient.Device();
        
        const rtpCapabilities = await new Promise((resolve) => {
          mediasoupSocket.current.emit("getRtpCapabilities", {}, (data) => {
            resolve(data.rtpCapabilities);
          });
        });
        
        await globalDevice.load({ routerRtpCapabilities: rtpCapabilities });
        console.log('🌍 GLOBALES MediaSoup Device initialisiert');

        const { params: transportOptions } = await new Promise((resolve) => {
          mediasoupSocket.current.emit("createWebRtcTransport", { direction: "recv" }, (data) => {
            resolve(data);
          });
        });

        const iceServers = await getIceServers(authToken);
        globalRecvTransport = globalDevice.createRecvTransport({ ...transportOptions, iceServers });
        console.log('🌍 GLOBALER Receive Transport erstellt:', globalRecvTransport.id);

        globalRecvTransport.on("connect", ({ dtlsParameters }, callback, errback) => {
          console.log('🔐 Globaler Transport verbindet...');
          mediasoupSocket.current.emit("connectTransport", {
            transportId: globalRecvTransport.id,
            dtlsParameters,
            direction: "recv",
          }, () => {
            console.log('✅ Globaler Transport connect callback aufgerufen');
            callback();
          });
        });
        
        let iceRestarting = false;
        const restartGlobalIce = async (state) => {
          if (iceRestarting || (state !== 'disconnected' && state !== 'failed')) return;
          iceRestarting = true;
          try {
            const { iceParameters } = await emitAsync(mediasoupSocket.current, 'restartIce', { transportId: globalRecvTransport.id });
            if (iceParameters) { await globalRecvTransport.restartIce({ iceParameters }); console.log('🧊 [GLOBAL] ICE-Restart ausgeführt'); }
          } catch (e) { console.error('❌ [GLOBAL] ICE-Restart fehlgeschlagen:', e.message); }
          finally { iceRestarting = false; }
        };
        globalRecvTransport.on('connectionstatechange', restartGlobalIce);
        globalRecvTransport.on('icestatechange', restartGlobalIce);

        isDeviceInitialized = true;
        return { device: globalDevice, transport: globalRecvTransport };
        
      } catch (error) {
        console.error('❌ Fehler bei Device-Initialisierung:', error);
        initDevicePromise = null; // Erlaube Retry bei Fehler
        throw error;
      }
      })(); // Ende initDevicePromise IIFE
      return await initDevicePromise;
    };

    const loadStudentStream = async (studentData) => {
      console.log("🔁 Neuer Student-Stream wird geladen:", studentData);
      
      // Überprüfe ob studentData korrekt ist
      if (!studentData || !studentData.studentSocketId || !studentData.studentNumber) {
        console.error("❌ Student-Data ist unvollständig oder undefined:", studentData);
        return;
      }
      
      const { studentSocketId, studentNumber } = studentData;
      
      // IMPROVED: Prüfe ob bereits ein VOLLSTÄNDIGER Stream existiert (video+audio)
      let existingStreamTrackCount = 0;
      for (let i = 0; i < 4; i++) {
        const videoEl = studentVideoRefs.current[i];
        if (videoEl && 
            videoEl.dataset.studentNumber == studentNumber && 
            videoEl.srcObject) {
          existingStreamTrackCount = videoEl.srcObject.getTracks().length;
          console.log(`🔍 Student ${studentNumber} hat bereits Stream in Slot ${i} mit ${existingStreamTrackCount} tracks`);
          break;
        }
      }
      
      // Erlaube Update wenn weniger als 2 Tracks vorhanden sind
      if (existingStreamTrackCount >= 2) {
        console.log(`✅ Student ${studentNumber} hat bereits vollständigen Stream (${existingStreamTrackCount} tracks), Skip`);
        return existingStreamTrackCount;
      }

      try {
        // Verwende GLOBALES Device und Transport
        const { device, transport } = await initializeDevice();

        // DEBUGGING: Transport-Status prüfen
        console.log(`🔍 [DEBUG] Transport connectionState: ${transport.connectionState}`);
        console.log(`🔍 [DEBUG] Transport iceConnectionState: ${transport.iceConnectionState}`);
        console.log(`🔍 [DEBUG] Transport dtlsParameters:`, transport.dtlsParameters);
        
        // Event-Listener für Transport-Verbindung
        transport.on('connectionstatechange', (state) => {
          console.log(`📡 [DEBUG] Student ${studentNumber} Transport connectionState changed: ${state}`);
        });
        transport.on('icestatechange', (state) => {
          console.log(`🧊 [DEBUG] Student ${studentNumber} ICE state changed: ${state}`);
        });

        const stream = new MediaStream();
        const audioStream = new MediaStream(); // separate stream for unmuted audio playback

        // Auf vorherige Verhandlungen warten, dann eigenen Platz in der Kette reservieren
        // (siehe consumeMutex-Deklaration oben) - verhindert parallele SDP-Verhandlung auf
        // dem geteilten globalRecvTransport, wenn mehrere Studenten kurz hintereinander beitreten.
        const previousConsume = consumeMutex;
        let releaseLock;
        consumeMutex = new Promise((resolve) => { releaseLock = resolve; });
        await previousConsume;

        try {
          const dataResp = await emitAsync(mediasoupSocket.current, "consume", {
            rtpCapabilities: device.rtpCapabilities,
            streamType: 'student',
            sourceSocketId: studentSocketId,
            transportId: transport.id,
          });
          const consumers = Array.isArray(dataResp.consumers) ? dataResp.consumers : [];
          if (consumers.length === 0) throw new Error('Keine Consumer erhalten');

          for (const consumeParams of consumers) {
            const consumer = await transport.consume({
              ...consumeParams,
              paused: false,
            });

            await consumer.resume();
            stream.addTrack(consumer.track);
            if (consumer.track.kind === 'audio') {
              audioStream.addTrack(consumer.track);
            }
            console.log(`✅ Student ${studentNumber} ${consumeParams.kind}-Consumer erstellt:`, consumer.id);

            // DEBUGGING: Consumer-Track-Status
            console.log(`🔍 [DEBUG] Consumer track: kind=${consumer.track.kind}, enabled=${consumer.track.enabled}, readyState=${consumer.track.readyState}, muted=${consumer.track.muted}`);
            console.log(`🔍 [DEBUG] Consumer paused:`, consumer.paused, ', closed:', consumer.closed);
          }
        } finally {
          releaseLock();
        }

        // Play student audio through a separate unmuted Audio element.
        // The <video> elements are muted (required for autoplay), so audio must be
        // routed via Audio() to actually reach the moderator's speakers.
        if (audioStream.getTracks().length > 0) {
          const existingAudioEl = studentAudioElements.current[studentNumber];
          if (existingAudioEl) {
            existingAudioEl.pause();
            existingAudioEl.srcObject = null;
          }
          const audioEl = new Audio();
          audioEl.srcObject = audioStream;
          audioEl.play().catch(e => console.error(`❌ Audio play error for student ${studentNumber}:`, e));
          studentAudioElements.current[studentNumber] = audioEl;
          console.log(`🔊 Student ${studentNumber} Audio wird über separates Audio-Element abgespielt`);
        }
        
        // DEBUGGING: Transport-Status NACH allen Consumern
        console.log(`🔍 [DEBUG] Transport connectionState AFTER consume: ${transport.connectionState}`);
        console.log(`🔍 [DEBUG] Transport iceConnectionState AFTER consume: ${transport.iceConnectionState}`);
        console.log(`🔍 [DEBUG] Transport id: ${transport.id}`);
        
        if (stream.getTracks().length > 0) {
          // FESTE SLOT-ZUORDNUNG: Jeder Student behält seinen festen Platz
          let targetVideoIndex = -1;
          
          // Prüfe zuerst, ob dieser Student bereits seinen Slot hat
          for (let i = 0; i < 4; i++) {
            const videoElement = studentVideoRefs.current[i];
            if (videoElement && videoElement.dataset.studentNumber == studentNumber) {
              targetVideoIndex = i;
              console.log(`🔄 Student ${studentNumber} verwendet bestehenden Slot ${i}`);
              break;
            }
          }
          
          // NEUE LOGIK: Jeder Student bekommt IMMER seinen festen Slot basierend auf studentNumber
          if (targetVideoIndex === -1) {
            targetVideoIndex = Math.min(studentNumber - 1, 3); // Student 1 → Slot 0, Student 2 → Slot 1, etc.
            console.log(`🏠 Student ${studentNumber} bekommt FESTEN Slot ${targetVideoIndex} (basierend auf studentNumber)`);
            
            // Prüfe ob der feste Slot bereits von einem anderen Student belegt ist
            const videoElement = studentVideoRefs.current[targetVideoIndex];
            if (videoElement && videoElement.dataset.studentNumber && 
                videoElement.dataset.studentNumber != studentNumber) {
              console.log(`⚠️ KONFLIKT: Slot ${targetVideoIndex} bereits von Student ${videoElement.dataset.studentNumber} belegt`);
              console.log(`🔄 Student ${studentNumber} überschreibt Slot ${targetVideoIndex} (feste Zuordnung hat Priorität)`);
            }
          }
          
          console.log(`🎯 FINALE Zuordnung: Student ${studentNumber} -> Video-Index ${targetVideoIndex}`);
          
          const videoElement = studentVideoRefs.current[targetVideoIndex];
          console.log(`🔍 [DEBUG] videoElement für Index ${targetVideoIndex}:`, videoElement);
          console.log(`🔍 [DEBUG] stream.getTracks():`, stream.getTracks().length, 'tracks');
          
          // DEBUGGING: Track-Status prüfen
          const tracks = stream.getTracks();
          tracks.forEach((track, i) => {
            console.log(`🔍 [DEBUG] Track ${i}: kind=${track.kind}, enabled=${track.enabled}, readyState=${track.readyState}, muted=${track.muted}, id=${track.id}`);
          });
          console.log(`🔍 [DEBUG] Stream active:`, stream.active);
          console.log(`🔍 [DEBUG] Stream id:`, stream.id);
          
          if (videoElement) {
            console.log(`🔍 [DEBUG] Vor srcObject-Zuweisung - aktuelles srcObject:`, videoElement.srcObject);
            videoElement.srcObject = stream;
            // Persistenter Stream-Speicher für Recovery
            studentStreamsRef.current[studentNumber] = stream;
            // muted bleibt auf dem Wert des JSX-Attributs (muted=true),
            // damit autoPlay auf allen Plattformen (Safari/iPad) zuverlässig funktioniert.
            // Unmuted autoplay erfordert auf Safari eine User-Gesture und führt sonst
            // zu play()-Fehlern → schwarzer Screen.
            videoElement.autoplay = true;
            videoElement.playsInline = true;
            videoElement.style.display = "block";
            videoElement.style.background = "#222";
            videoElement.style.width = "100%";
            videoElement.style.height = "100%";
            
            // WICHTIG: Markiere Video-Element mit Student-Nummer VOR play()
            videoElement.dataset.studentNumber = studentNumber;
            console.log(`🔍 [DEBUG] Nach srcObject-Zuweisung für Student ${studentNumber}, dataset.studentNumber=${videoElement.dataset.studentNumber}`);
            
            // DEBUGGING: Event-Listener für Video-Element
            videoElement.onloadedmetadata = () => {
              console.log(`📊 [DEBUG] Student ${studentNumber} onloadedmetadata: readyState=${videoElement.readyState}, networkState=${videoElement.networkState}`);
            };
            videoElement.onloadeddata = () => {
              console.log(`📊 [DEBUG] Student ${studentNumber} onloadeddata: readyState=${videoElement.readyState}`);
            };
            videoElement.oncanplay = () => {
              console.log(`📊 [DEBUG] Student ${studentNumber} oncanplay: can play now`);
            };
            videoElement.onerror = (e) => {
              console.error(`❌ [DEBUG] Student ${studentNumber} video error:`, e, videoElement.error);
            };
            
            // RACE CONDITION FIX: Prüfe ob Element noch den richtigen Student hat bevor play()
            console.log(`🔍 [DEBUG] CHECKING Race Condition: videoElement.dataset.studentNumber="${videoElement.dataset.studentNumber}" vs studentNumber=${studentNumber}`);
            console.log(`🔍 [DEBUG] Comparison types: dataset type=${typeof videoElement.dataset.studentNumber}, studentNumber type=${typeof studentNumber}`);
            console.log(`🔍 [DEBUG] Loose comparison (!=): ${videoElement.dataset.studentNumber != studentNumber}`);
            console.log(`🔍 [DEBUG] Strict comparison (!==): ${videoElement.dataset.studentNumber !== studentNumber}`);
            
            if (videoElement.dataset.studentNumber != studentNumber) {
              console.warn(`⚠️ Race Condition: Student ${studentNumber} wurde bereits disconnected, breche Stream-Setup ab`);
              return; // Abbruch - Student hat sich bereits getrennt
            }
            
            console.log(`✅ [DEBUG] Race Condition Check PASSED, proceeding to play()`);

            console.log(`🎥 Student ${studentNumber} Stream zugewiesen zu Video-Element ${targetVideoIndex}`, videoElement);

            // State sofort aktualisieren (vor play()), damit Video-Slot sichtbar wird
            // auch wenn play() auf Safari/iPad durch Autoplay-Policy fehlschlägt
            setConnectedStudents(prev => {
              const existingStudentIndex = prev.findIndex(student => 
                student && student.studentNumber === studentNumber
              );
              
              if (existingStudentIndex >= 0) {
                // Student existiert bereits - nur hasVideo aktualisieren
                console.log(`🔄 Aktualisiere bestehenden Student ${studentNumber} hasVideo=true`);
                return prev.map((student, index) => 
                  index === existingStudentIndex 
                    ? { ...student, hasVideo: true }
                    : student
                );
              } else {
                // Student existiert noch nicht - neuen Student hinzufügen
                console.log(`➕ Füge neuen Student ${studentNumber} hinzu mit hasVideo=true`);
                const newStudent = {
                  studentNumber: studentNumber,
                  socketId: studentSocketId,
                  hasVideo: true,
                  name: studentData.studentName || `Student ${studentNumber}`,
                  connected: true
                };
                return [...prev, newStudent];
              }
            });
            
            console.log(`✅ Student ${studentNumber} Video-Status aktualisiert`);

            // play() nach State-Update: Fehler loggen aber nicht abbrechen
            // Safari/iPad lehnt play() auf unmuted Video ohne User-Gesture ab (NotAllowedError),
            // aber der Stream ist bereits sichtbar dank setConnectedStudents oben
            try {
              console.log(`🎬 [DEBUG] CALLING videoElement.play() NOW...`);
              const playPromise = videoElement.play();
              const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('play() timeout after 30s')), 30000)
              );
              await Promise.race([playPromise, timeoutPromise]);
              console.log(`✅ Student ${studentNumber} Video-Element play() erfolgreich!`);

              // Prüfe ob Student während play() disconnected wurde
              const streamKey = `${studentNumber}-${studentSocketId}`;
              const streamState = processedStreams.get(streamKey);
              if (streamState && streamState.shouldCleanup) {
                console.warn(`🧹 Student ${studentNumber} wurde während play() disconnected - führe verzögertes Cleanup aus`);
                videoElement.srcObject = null;
                delete videoElement.dataset.studentNumber;
                processedStreams.delete(streamKey);
                return 0;
              }
            } catch (err) {
              console.error(`❌ Student ${studentNumber} play() Fehler (Video bleibt sichtbar):`, err.name, err.message);
              // Kein return — Video-Slot ist bereits sichtbar, Stream läuft ggf. stumm weiter
            }
          } else {
            console.warn(`❌ Video-Element für Student ${studentNumber} nicht gefunden`, videoElement);
          }
        } else {
          console.warn(`⚠️ Kein Stream für Student ${studentNumber} erhalten`);
        }
        
        // WICHTIG: Return trackCount für processedStreams-Tracking
        return stream.getTracks().length;

      } catch (error) {
        console.error(`❌ Fehler beim Laden des Student ${studentNumber} Streams:`, error);
        return 0; // Bei Fehler, return 0
      }
    };

    // Handler für studentDisconnected Event
    const handleStudentDisconnected = (data) => {
      console.log("👋 Student getrennt:", data);
      const { studentSocketId, studentNumber } = data;
      
      // KORRIGIERT: Remove student from connected list - NUR den spezifischen Student entfernen
      setConnectedStudents(prev => {
        const filtered = prev.filter(s => {
          // Entferne nur Studenten, die SOWOHL dieselbe socketId ODER dieselbe studentNumber haben
          const shouldRemove = s.socketId === studentSocketId || s.studentNumber === studentNumber;
          if (shouldRemove) {
            console.log(`🗑️ Removing student: socketId=${s.socketId}, studentNumber=${s.studentNumber} (reason: ${s.socketId === studentSocketId ? 'socketId match' : 'studentNumber match'})`);
          }
          return !shouldRemove;
        });
        console.log(`👋 After disconnect filter: ${prev.length} -> ${filtered.length} students remaining`);
        return filtered;
      });
      
      // WICHTIG: Prüfe ob Stream gerade geladen wird (Race Condition Prevention)
      const streamKey = `${studentNumber}-${studentSocketId}`;
      const streamState = processedStreams.get(streamKey);
      
      if (streamState && streamState.loading) {
        console.warn(`⏸️ Student ${studentNumber} disconnected während Stream geladen wird - überspringe Video-Element Cleanup (wird nach play() erledigt)`);
        // Markiere für späteren Cleanup NACH play()
        processedStreams.set(streamKey, { ...streamState, shouldCleanup: true });
        return; // WICHTIG: Nicht das Video-Element leeren während loadStudentStream() läuft
      }
      
      // Clear video element nur wenn NICHT gerade loading
      const videoIndex = studentNumber - 1;
      const videoElement = studentVideoRefs.current[videoIndex];
      if (videoElement) {
        videoElement.srcObject = null;
        delete videoElement.dataset.studentNumber; // WICHTIG: Auch dataset clearen
        console.log(`🗑️ Video-Element ${videoIndex} für Student ${studentNumber} geleert`);
      }
      
      // ZUSÄTZLICH: Suche nach Video-Element über dataset.studentNumber (falls nicht an Index studentNumber-1)
      for (let i = 0; i < 4; i++) {
        const videoEl = studentVideoRefs.current[i];
        if (videoEl && videoEl.dataset.studentNumber == studentNumber) {
          videoEl.srcObject = null;
          delete videoEl.dataset.studentNumber;
          console.log(`🗑️ Video-Element ${i} für Student ${studentNumber} geleert (via dataset search)`);
          break;
        }
      }
      
      // Stop and remove the unmuted audio element for this student
      const audioEl = studentAudioElements.current[studentNumber];
      if (audioEl) {
        audioEl.pause();
        audioEl.srcObject = null;
        delete studentAudioElements.current[studentNumber];
        console.log(`🗑️ Audio-Element für Student ${studentNumber} gestoppt`);
      }

      // CLEAR DEDUPLICATION für diesen Student
      if (data && data.studentNumber && data.studentSocketId) {
        processedStreams.delete(streamKey);
        console.log(`🗑️ Deduplication cleared für Student ${data.studentNumber}`);
      }
    };

    // Console log before registering the listener
    console.log("⏳ Warte auf Producer-Signal zum Konsumieren...");
    
    mediasoupSocket.current.on("studentStream", (data) => {
      console.log("🎉 StudentStream-Event empfangen!", data);
      
      if (!data || !data.studentNumber || !data.studentSocketId) {
        console.warn("⚠️ Ignoriere unvollständiges studentStream Event:", data);
        return;
      }
      
      const streamKey = `${data.studentNumber}-${data.studentSocketId}`;
      const existingStream = processedStreams.get(streamKey);
      
      // VERBESSERTE DEDUPLICATION: Erlaube Update wenn:
      // 1. Stream wird gerade geladen (loading=true) → warte
      // 2. Stream hat weniger als 2 Tracks (video+audio) → erlaube Update
      // 3. Stream ist älter als 5 Sekunden → erlaube Retry
      
      if (existingStream) {
        const now = Date.now();
        const age = now - existingStream.timestamp;
        
        if (existingStream.loading) {
          console.log(`⏳ Stream für Student ${data.studentNumber} wird bereits geladen, überspringe Duplikat`);
          return;
        }
        
        if (existingStream.trackCount >= 2 && age < 5000) {
          console.log(`⚠️ Stream für Student ${data.studentNumber} bereits komplett (${existingStream.trackCount} tracks), überspringe Duplikat`);
          return;
        }
        
        if (existingStream.trackCount < 2) {
          console.log(`🔄 Stream für Student ${data.studentNumber} hat nur ${existingStream.trackCount} tracks, erlaube Update`);
        } else if (age >= 5000) {
          console.log(`🔄 Stream für Student ${data.studentNumber} ist ${Math.round(age/1000)}s alt, erlaube Retry`);
        }
      }
      
      processedStreams.set(streamKey, { loading: true, trackCount: 0, timestamp: Date.now() });
      console.log(`🔄 Verarbeite neuen Stream für Student ${data.studentNumber}`);

      withRetry(() => loadStudentStream(data), { label: `student-${data.studentNumber}-consume`, retries: 5, base: 500, max: 8000 })
        .then((trackCount) => {
          processedStreams.set(streamKey, { loading: false, trackCount: trackCount || 0, timestamp: Date.now() });
          console.log(`✅ Stream für Student ${data.studentNumber} geladen mit ${trackCount || 0} tracks`);
        })
        .catch((e) => {
          // State zurücksetzen, damit ein erneutes studentStream-Event oder requestStreams neu lädt.
          processedStreams.delete(streamKey);
          console.error(`❌ Stream für Student ${data.studentNumber} endgültig fehlgeschlagen:`, e.message);
        });
    });

    mediasoupSocket.current.on("studentDisconnected", (data) => {
      console.log("👋 StudentDisconnected-Event empfangen!", data);
      handleStudentDisconnected(data);
    });

    mediasoupSocket.current.on("studentNameUpdate", (data) => {
      console.log("📝 StudentNameUpdate-Event empfangen!", data);
      const { studentSocketId, studentNumber, studentName } = data;

      // Update connectedStudents with new name
      setConnectedStudents(prev => {
        return prev.map(student => {
          if (student.socketId === studentSocketId || student.studentNumber === studentNumber) {
            return { ...student, name: studentName };
          }
          return student;
        });
      });
    });

    // ✅ Hospitations-Icon: Observer-Events
    mediasoupSocket.current.on('observerPresent', ({ count }) => dispatchObserver({ type: 'observerPresent', count }));
    mediasoupSocket.current.on('observerLeft',    ({ count }) => dispatchObserver({ type: 'observerLeft', count }));

    // Automatisch Stream starten nach kurzer Verzögerung
    setTimeout(() => {
      startStream().catch(console.error);
    }, 2000);

    // Stream-Recovery Intervall: prüfe alle 3s ob Streams noch aktiv sind
    const streamRecoveryInterval = setInterval(() => {
      // Moderator-eigenen Stream wiederherstellen falls srcObject verloren
      if (window.modCameraStream && moderatorVideoRef.current && !moderatorVideoRef.current.srcObject) {
        console.log('🔄 [Recovery] Moderator-Stream neu anhängen');
        moderatorVideoRef.current.srcObject = window.modCameraStream;
        moderatorVideoRef.current.play().catch(() => {});
      }

      // Student-Streams wiederherstellen falls srcObject verloren
      Object.entries(studentStreamsRef.current).forEach(([studentNumber, stream]) => {
        const videoIndex = parseInt(studentNumber, 10) - 1;
        const videoEl = studentVideoRefs.current[videoIndex];
        if (videoEl && stream && stream.active && !videoEl.srcObject) {
          console.log(`🔄 [Recovery] Stream für Student ${studentNumber} neu anhängen`);
          videoEl.srcObject = stream;
          videoEl.play().catch(() => {});
        }
      });

      // Student-Audio wiederherstellen: audio.play() kann durch Autoplay-Policy blockiert
      // worden sein wenn der Schüler vor dem Mod beigetreten ist (noch keine User-Gesture).
      // Retry bei jeder Recovery-Runde – sobald eine User-Gesture stattgefunden hat,
      // wird play() erfolgreich sein.
      Object.values(studentAudioElements.current).forEach(audioEl => {
        if (audioEl && audioEl.paused && audioEl.srcObject) {
          audioEl.play().catch(() => {});
        }
      });
    }, 3000);

    return () => {
      clearInterval(streamRecoveryInterval);
      if (mediasoupSocket.current) {
        mediasoupSocket.current.off("studentStream");
        mediasoupSocket.current.off("studentDisconnected");
        mediasoupSocket.current.disconnect();
      }
    };
  }, [roomId, authToken, authValidated]);

  // Memoize video grid so it only re-renders when connectedStudents changes,
  // NOT when questions/other state changes — prevents WebRTC stream freezes.
  const studentVideoSlots = useMemo(() => {
    const rawStudents = Array.isArray(connectedStudents) ? connectedStudents : [];
    const socketStudents = rawStudents.filter(s => s && s.studentNumber && !s.hasVideo);
    const videoStudents = rawStudents.filter(s => s && s.studentNumber && s.hasVideo);

    const allStudentNumbers = new Set();
    rawStudents.forEach(s => { if (s && s.studentNumber) allStudentNumbers.add(s.studentNumber); });

    const normalizedStudents = Array.from(allStudentNumbers).map(studentNumber => {
      const videoStudent = videoStudents.find(s => s.studentNumber === studentNumber);
      if (videoStudent) {
        return { studentNumber: videoStudent.studentNumber, name: videoStudent.name || `Student ${videoStudent.studentNumber}`, hasVideo: true, socketId: videoStudent.socketId || videoStudent.id, joinedAt: videoStudent.joinedAt };
      }
      const socketStudent = socketStudents.find(s => s.studentNumber === studentNumber);
      if (socketStudent) {
        return { studentNumber: socketStudent.studentNumber, name: socketStudent.name || `Student ${socketStudent.studentNumber}`, hasVideo: false, socketId: socketStudent.socketId || socketStudent.id, joinedAt: socketStudent.joinedAt };
      }
      return null;
    }).filter(Boolean).sort((a, b) => a.studentNumber - b.studentNumber);

    const totalSlotsToShow = 4;
    const slotStudents = Array.from({ length: totalSlotsToShow }, (_, i) =>
      normalizedStudents.find(s => s.studentNumber === i + 1) || null
    );

    return Array.from({ length: totalSlotsToShow }, (_, index) => {
      const student = slotStudents[index];
      const hasValidStudent = student !== null;
      const hasVideo = student?.hasVideo || false;
      return (
        <div key={index} className={`studentVideoWrapper ${hasValidStudent ? 'active' : 'empty'}`}>
          <video
            ref={el => { studentVideoRefs.current[index] = el; }}
            autoPlay
            muted
            className="studentVideo"
            style={{ display: hasVideo ? 'block' : 'none' }}
          />
          {hasValidStudent && !hasVideo && (
            <div className="audioOnlyPlaceholder" style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', height: '100%', color: '#aaa', fontSize: '0.9em', gap: '6px'
            }}>
              <span style={{ fontSize: '2em' }}>🎤</span>
              <span>Nur Audio</span>
            </div>
          )}
          <div className="studentVideoLabel">
            {hasValidStudent ? (getFirstName(student.name) || student.name || `Platz ${index + 1}`) : `Platz ${index + 1}`}
          </div>
          {!hasValidStudent && <div className="emptySlot">Wartet...</div>}
        </div>
      );
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectedStudents]);

  // Warte auf Token-Validierung oder zeige Fehler
  if (authError) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
        color: 'white',
        fontFamily: 'Arial, sans-serif'
      }}>
        <div style={{ textAlign: 'center', maxWidth: '500px', padding: '30px' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>🔒</div>
          <div style={{ fontSize: '24px', marginBottom: '20px', fontWeight: 'bold' }}>Zugriff verweigert</div>
          <div style={{ fontSize: '16px', marginBottom: '30px', lineHeight: '1.6' }}>{authError}</div>
          <a href="https://okadia.de/dashboard.php" style={{
            display: 'inline-block',
            background: 'white',
            color: '#e74c3c',
            padding: '12px 30px',
            borderRadius: '25px',
            textDecoration: 'none',
            fontWeight: 'bold',
            fontSize: '16px'
          }}>
            Zurück zum Dashboard
          </a>
        </div>
      </div>
    );
  }
  
  // Buch-Ladeanimation auf Weiß während der Token-Validierung (identisch zur
  //   Schüler-Ansicht in App.js). Bleibt stehen, bis authValidated UND der
  //   Logo-Aufbau mindestens einmal komplett gezeigt wurde.
  if (!authValidated || !loaderMinShown) {
    return <BookLoaderScreen onDrawn={() => setLoaderMinShown(true)} />;
  }

  return (
    <div className="newModeratorLayout">
      {/* === NEUE LAYOUT-STRUKTUR === */}

      {/* Schüler-Videos - ganz oben, nebeneinander */}
      <div className="studentsVideoSection">
        <div className="studentsVideoHeader">
          <h3>
            Schüler ({Array.isArray(connectedStudents) ? connectedStudents.filter(s => s && s.hasVideo).length : 0})
            {isObserverVisible(observerState) && (
              <span
                className="hospitation-indicator"
                title="Hospitation zur Qualitätssicherung"
                aria-label="Ein Administrator hospitiert diese Session"
                style={{ marginLeft: 8, fontSize: '1.2rem', cursor: 'help' }}
              >
                👁️
              </span>
            )}
          </h3>
        </div>
        <div className="studentsVideoGrid">
          {studentVideoSlots}
        </div>
      </div>

      {/* Hauptbereich - 3 Spalten */}
      <div className="mainContentArea">
        
        {/* LINKE SPALTE: Moderator-Kamera und Stats */}
        <div className="leftPanel">
          <div className="moderatorVideoSection">
            <h4>Moderator</h4>
            <div className="moderatorVideoBox">
              <video ref={moderatorVideoRef} autoPlay className="moderatorVideo" />
              <div className="modMediaControls">
                <button
                  type="button"
                  className={`modMediaBtn${audioMuted ? ' muted' : ''}`}
                  onClick={toggleAudioMute}
                  disabled={!streamStarted}
                  aria-pressed={audioMuted}
                  title={audioMuted ? 'Mikrofon einschalten' : 'Mikrofon stummschalten'}
                >
                  {audioMuted ? '🔇' : '🎤'}
                </button>
                <button
                  type="button"
                  className={`modMediaBtn${videoMuted ? ' muted' : ''}`}
                  onClick={toggleVideoMute}
                  disabled={!streamStarted}
                  aria-pressed={videoMuted}
                  title={videoMuted ? 'Kamera einschalten' : 'Kamera ausschalten'}
                >
                  {videoMuted ? '🚫' : '🎥'}
                </button>
              </div>
            </div>
          </div>
          
          <div className="moderatorStatsSection">
            <h4>Statistiken</h4>
            <div className="statsContainer">
              <div className="statTile progress" style={{ gridColumn: '1 / -1' }}>
                <span className="statNumber">{currentIndex + 1}/{activeQuestions.length}</span>
                <span className="statLabel">Frage</span>
              </div>
            </div>
            <div style={{ marginTop: '10px' }}>
              <h5 style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#555' }}>Ergebnisse pro Schüler</h5>
              {connectedStudents.length === 0 ? (
                <p style={{ fontSize: '12px', color: '#999' }}>Keine Schüler verbunden</p>
              ) : (
                connectedStudents.map(student => {
                  const score = studentScores[student.studentNumber] || { correct: 0, wrong: 0 };
                  return (
                    <div key={student.studentNumber} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '5px 8px', marginBottom: '4px', borderRadius: '6px',
                      background: '#f8f9fa', border: '1px solid #e9ecef', fontSize: '13px'
                    }}>
                      <span style={{ fontWeight: 500 }}>{getFirstName(student.name) || student.name || `Schüler ${student.studentNumber}`}</span>
                      <span>
                        <span style={{ color: '#28a745', fontWeight: 'bold' }}>✓{score.correct}</span>
                        {' '}
                        <span style={{ color: '#dc3545', fontWeight: 'bold' }}>✗{score.wrong}</span>
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* MITTLERE SPALTE: Aktuelle Frage */}
        <div className="centerPanel">
          {currentView === 'whiteboard' ? (
            <div className="wb-stage">
              <Whiteboard socketRef={socket} roomId={roomId} readOnly={false} onUploadImage={uploadWhiteboardImage} />
            </div>
          ) : currentQuestion && (
            <div className="questionDisplaySection">
              <div className="questionContent">
                <h3>{currentQuestion.question}</h3>
                
{renderQuestionForModerator()}
                
              </div>
            </div>
          )}
        </div>

        {/* RECHTE SPALTE: View-Slider und Controls */}
        <div className="rightPanel">
          <div className="sliderSection">
            <h4>Ansicht</h4>
            <div style={{ position: 'relative' }}>
              <ViewSlider
                currentView={currentView}
                onViewChange={handleViewChange}
              />
              {currentView === 'success' && (
                <div style={{
                  position: 'absolute', inset: 0,
                  background: 'rgba(255,255,255,0.7)',
                  borderRadius: '8px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.75rem', color: '#666', fontStyle: 'italic',
                  pointerEvents: 'none'
                }}>
                  🔒 Stunde beendet
                </div>
              )}
            </div>
          </div>
          
          <div className="controlsSection">
            <h4>Steuerung</h4>
            <div className="controlButtons">
              <div className="controlBtnRow">
              <button
                onClick={() => { const t = Math.max(currentIndex - 1, 0); setCurrentIndex(t); socket.current.emit("moderatorSetQuestion", { roomId, questionIndex: t }); }}
                className="controlBtn prevBtn"
                disabled={currentIndex === 0}
              >
                ← Zurück
              </button>

              <button
                onClick={() => { const t = Math.min(currentIndex + 1, Math.max(activeQuestions.length - 1, 0)); setCurrentIndex(t); socket.current.emit("moderatorSetQuestion", { roomId, questionIndex: t }); }}
                className="controlBtn skipBtn"
              >
                weiter
              </button>
              </div>

              <div className="controlBtnRow">
              <button
                onClick={() => {
                  setShowQuestionManager(true);
                  loadAllQuestions();
                }}
                className="controlBtn manageBtn"
              >
                📝 Fragen
              </button>

              <button
                onClick={() => {
                  setShowQuestionBrowser(true);
                  setBrowserSelectedQuestion(null);
                }}
                className="controlBtn manageBtn"
              >
                📋 Übersicht
              </button>
              </div>

              <button
                onClick={() => {
                  const newValue = !allowStudentNavigation;
                  setAllowStudentNavigation(newValue);
                  console.log(`🎯 Navigation-Kontrolle: ${newValue ? 'aktiviert' : 'deaktiviert'}`);
                  
                  // Sende Update an alle Schüler
                  if (socket.current?.connected) {
                    socket.current.emit('navigationControlUpdate', {
                      roomId: roomId,
                      allowNavigation: newValue
                    });
                  }
                }} 
                className={`controlBtn navControlBtn ${!allowStudentNavigation ? 'locked' : ''}`}
                style={{
                  backgroundColor: allowStudentNavigation ? '#28a745' : '#dc3545',
                  color: 'white',
                  fontWeight: 'bold',
                  border: 'none',
                  transition: 'all 0.3s ease'
                }}
                title={allowStudentNavigation ? 'Schüler-Navigation erlaubt - Klicken zum Sperren' : 'Schüler-Navigation gesperrt - Klicken zum Erlauben'}
              >
                {allowStudentNavigation ? '🔓 Schüler-Nav EIN' : '🔒 Schüler-Nav AUS'}
              </button>
            </div>
            <div className="calcMenuWrap">
              {calcMenuOpen && (
                <div className="calcMenuBackdrop" onClick={() => setCalcMenuOpen(false)} />
              )}
              <button
                className={`controlBtn calcToggleBtn ${calcMenuOpen ? 'active' : ''}`}
                onClick={() => setCalcMenuOpen(v => !v)}
                aria-expanded={calcMenuOpen}
              >
                🔢 Rechner {calcMenuOpen ? '▾' : '▴'}
              </button>
              {calcMenuOpen && (
                <div className="calcPopover" role="menu">
                  <div className="calcControlLabel">🔢 Schüler-Rechner</div>
                  <CalculatorModeSlider mode={calculatorMode} onChange={handleCalculatorModeChange} />
                  <div className="calcOwnButtons">
                    <button
                      className={`controlBtn ${ownBasicOpen ? 'active' : ''}`}
                      onClick={() => setOwnBasicOpen(v => !v)}
                    >🧮 Basic</button>
                    <button
                      className={`controlBtn ${ownFullOpen ? 'active' : ''}`}
                      onClick={() => setOwnFullOpen(v => !v)}
                    >📐 Vollwertig</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {ownBasicOpen && (
        <CalculatorWindow title="Basic-Rechner" onClose={() => setOwnBasicOpen(false)}
          initialPosition={{ x: 60, y: 100 }}>
          <BasicCalculator />
        </CalculatorWindow>
      )}
      {ownFullOpen && (
        <CalculatorWindow title="Vollwertiger Rechner" onClose={() => setOwnFullOpen(false)}
          initialPosition={{ x: 120, y: 140 }}>
          <GeoGebraCalculator />
        </CalculatorWindow>
      )}

      {/* Fragen-Management Overlay */}
      {showQuestionManager && (
        <div className="questionManagerOverlay">
          <div className="questionManagerPanel">
            <div className="questionManagerHeader">
              <h2>Fragen verwalten</h2>
              <button 
                className="closeButton"
                onClick={() => setShowQuestionManager(false)}
              >
                ✕
              </button>
            </div>

            <div className="questionManagerContent">
              {/* Nächste 5 Fragen Preview */}
              <div className="nextQuestionsSection">
                <h3>Nächste 5 Fragen</h3>
                <div className="questionPreviewList">
                  {activeQuestions.slice(currentIndex + 1, currentIndex + 6).map((q, index) => (
                    <div key={currentIndex + 1 + index} className="previewQuestionCard">
                      <p className="previewQuestionNumber">Frage {currentIndex + 2 + index}</p>
                      <p className="previewQuestionText">{q.question}</p>
                      {q.image && (
                        <img src={q.image} alt="Vorschau" className="previewQuestionImage" />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Erweiterte Fragen-Verwaltung */}
              <div className="questionManagerSection">
                <h3>Fragen-Manager</h3>
                <div style={{display: 'flex', gap: '20px', minHeight: '400px'}}>
                  {/* Linke Spalte: Aktuelle Fragen */}
                  <div style={{flex: 1, background: '#f8f9fa', padding: '20px', borderRadius: '8px', border: '2px solid #e9ecef'}}>
                    <h4 style={{marginTop: 0, color: '#495057', fontSize: '16px'}}>Aktuelle Fragen</h4>
                    
                    <div style={{minHeight: '200px'}}>
                      {questions.length === 0 ? (
                        <div style={{textAlign: 'center', color: '#6c757d', padding: '40px'}}>
                          Noch keine Fragen hinzugefügt
                        </div>
                      ) : (
                        questions.map((question, index) => (
                          <div 
                            key={question.id}
                            style={{
                              background: question.disabled ? '#f0f0f0' : 'white',
                              padding: '15px',
                              marginBottom: '10px',
                              borderRadius: '6px',
                              border: question.disabled ? '1px solid #ccc' : '1px solid #dee2e6',
                              opacity: question.disabled ? 0.6 : 1
                            }}
                          >
                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'start'}}>
                              <div style={{flex: 1}}>
                                <div style={{
                                  fontWeight: 500,
                                  color: question.disabled ? '#999' : '#495057',
                                  marginBottom: '5px',
                                  textDecoration: question.disabled ? 'line-through' : 'none'
                                }}>
                                  {question.disabled ? '🚫' : '❓'} {question.question}
                                </div>
                                <div style={{fontSize: '12px', color: '#6c757d'}}>
                                  Typ: {question.type} | Position: {index + 1}
                                  {question.disabled && <span style={{marginLeft: '10px', color: '#dc3545', fontWeight: 'bold'}}>DEAKTIVIERT</span>}
                                </div>
                              </div>
                              <div style={{marginLeft: '10px'}}>
                                {question.disabled ? (
                                  <button 
                                    onClick={() => reactivateQuestionInSession(question.id)}
                                    style={{
                                      background: '#28a745',
                                      color: 'white',
                                      border: 'none',
                                      padding: '4px 8px',
                                      borderRadius: '3px',
                                      cursor: 'pointer',
                                      fontSize: '11px'
                                    }}
                                    title="Frage reaktivieren"
                                  >
                                    ✅ reaktivieren
                                  </button>
                                ) : (
                                  <button 
                                    onClick={() => removeQuestionFromSession(index)}
                                    style={{
                                      background: '#dc3545',
                                      color: 'white',
                                      border: 'none',
                                      padding: '4px 8px',
                                      borderRadius: '3px',
                                      cursor: 'pointer',
                                      fontSize: '11px'
                                    }}
                                    title="Frage deaktivieren"
                                  >
                                    ❌ deaktivieren
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                  
                  {/* Rechte Spalte: Aktionen */}
                  <div style={{flex: 1, background: '#fff', padding: '20px', border: '2px solid #e9ecef', borderRadius: '8px'}}>
                    <h4 style={{marginTop: 0, color: '#495057', fontSize: '16px'}}>
                      {questionManagerView === 'main' && 'Fragen hinzufügen'}
                      {questionManagerView === 'stapel' && 'Fragen-Stapel auswählen'}
                      {questionManagerView === 'einzelfragen' && 'Einzelfrage auswählen'}
                      {questionManagerView === 'erstellen' && 'Neue Frage erstellen'}
                    </h4>
                    
                    {/* Haupt-Aktionen */}
                    {questionManagerView === 'main' && (
                      <div style={{display: 'flex', flexDirection: 'column', gap: '15px'}}>
                        <button 
                          style={{
                            background: '#007bff',
                            color: 'white',
                            padding: '15px',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                          onClick={() => {
                            setQuestionManagerView('stapel');
                            loadStapelListe();
                            setFilterSchwierigkeit(''); setFilterKlasse(''); setFilterFach('');
                          }}
                        >
                          📚 Fragen-Stapel hinzufügen
                        </button>
                        
                        <button 
                          style={{
                            background: '#28a745',
                            color: 'white',
                            padding: '15px',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                          onClick={() => {
                            setQuestionManagerView('einzelfragen');
                            loadEinzelfragenListe();
                            setFilterSchwierigkeit(''); setFilterKlasse(''); setFilterFach('');
                          }}
                        >
                          ➕ Einzelfrage hinzufügen
                        </button>
                        
                        <button 
                          style={{
                            background: '#17a2b8',
                            color: 'white',
                            padding: '15px',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                          onClick={() => setQuestionManagerView('erstellen')}
                        >
                          ✏️ Neue Frage erstellen
                        </button>
                      </div>
                    )}

                    {/* Stapel-Auswahl */}
                    {questionManagerView === 'stapel' && (
                      <div>
                        <div style={{marginBottom: '15px'}}>
                          <button 
                            onClick={() => setQuestionManagerView('main')}
                            style={{
                              background: '#6c757d',
                              color: 'white',
                              padding: '8px 15px',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            ← Zurück
                          </button>
                        </div>
                        
                        <div style={{marginBottom: '15px'}}>
                          <input 
                            type="text"
                            value={stapelSuche}
                            onChange={(e) => setStapelSuche(e.target.value)}
                            placeholder="Stapel suchen..."
                            style={{
                              width: '100%',
                              padding: '10px',
                              border: '2px solid #e9ecef',
                              borderRadius: '6px',
                              fontSize: '14px'
                            }}
                          />
                        </div>
                        
                        <div style={{display: 'flex', gap: '8px', marginBottom: '15px', flexWrap: 'wrap'}}>
                          <select value={filterSchwierigkeit} onChange={(e) => setFilterSchwierigkeit(e.target.value)} style={{flex: 1, minWidth: '110px', padding: '8px', border: '2px solid #e9ecef', borderRadius: '6px', fontSize: '13px'}}>
                            <option value="">Alle Schwierigkeiten</option>
                            {[...new Set(stapelListe.map(s => s.schwierigkeitsgrad).filter(Boolean))].sort().map(v => <option key={v} value={v}>{v}</option>)}
                          </select>
                          <select value={filterKlasse} onChange={(e) => setFilterKlasse(e.target.value)} style={{flex: 1, minWidth: '110px', padding: '8px', border: '2px solid #e9ecef', borderRadius: '6px', fontSize: '13px'}}>
                            <option value="">Alle Klassen</option>
                            {[...new Set(stapelListe.map(s => s.klasse).filter(Boolean))].sort().map(v => <option key={v} value={v}>{v}</option>)}
                          </select>
                          <select value={filterFach} onChange={(e) => setFilterFach(e.target.value)} style={{flex: 1, minWidth: '110px', padding: '8px', border: '2px solid #e9ecef', borderRadius: '6px', fontSize: '13px'}}>
                            <option value="">Alle Fächer</option>
                            {[...new Set(stapelListe.map(s => s.fach_name).filter(Boolean))].sort().map(v => <option key={v} value={v}>{v}</option>)}
                          </select>
                        </div>

                        <div style={{maxHeight: '300px', overflowY: 'auto'}}>
                          {stapelListe.filter(stapel =>
                            (stapel.name.toLowerCase().includes(stapelSuche.toLowerCase()) ||
                             (stapel.beschreibung && stapel.beschreibung.toLowerCase().includes(stapelSuche.toLowerCase()))) &&
                            (!filterSchwierigkeit || stapel.schwierigkeitsgrad === filterSchwierigkeit) &&
                            (!filterKlasse || stapel.klasse === filterKlasse) &&
                            (!filterFach || stapel.fach_name === filterFach)
                          ).map(stapel => (
                            <div 
                              key={stapel.id}
                              style={{
                                background: '#f8f9fa',
                                border: '2px solid #e9ecef',
                                borderRadius: '6px',
                                padding: '15px',
                                marginBottom: '10px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onClick={() => {
                                setPendingConfirm({ type: 'stapel', id: stapel.id, name: stapel.name });
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.borderColor = '#007bff';
                                e.target.style.backgroundColor = '#e3f2fd';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.borderColor = '#e9ecef';
                                e.target.style.backgroundColor = '#f8f9fa';
                              }}
                            >
                              <div style={{fontWeight: 500, color: '#495057', marginBottom: '5px'}}>
                                {stapel.name}
                              </div>
                              <div style={{fontSize: '12px', color: '#6c757d', marginBottom: '8px'}}>
                                {stapel.anzahl_fragen || 0} Fragen
                                {stapel.schwierigkeitsgrad && ` | ${stapel.schwierigkeitsgrad}`}
                              </div>
                              {stapel.beschreibung && (
                                <div style={{fontSize: '11px', color: '#868e96'}}>
                                  {stapel.beschreibung.length > 100 
                                    ? stapel.beschreibung.substring(0, 100) + '...'
                                    : stapel.beschreibung
                                  }
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Einzelfragen-Auswahl */}
                    {questionManagerView === 'einzelfragen' && (
                      <div>
                        <div style={{marginBottom: '15px'}}>
                          <button 
                            onClick={() => setQuestionManagerView('main')}
                            style={{
                              background: '#6c757d',
                              color: 'white',
                              padding: '8px 15px',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            ← Zurück
                          </button>
                        </div>
                        
                        <div style={{marginBottom: '15px'}}>
                          <input 
                            type="text"
                            value={fragenSuche}
                            onChange={(e) => setFragenSuche(e.target.value)}
                            placeholder="Fragen suchen..."
                            style={{
                              width: '100%',
                              padding: '10px',
                              border: '2px solid #e9ecef',
                              borderRadius: '6px',
                              fontSize: '14px'
                            }}
                          />
                        </div>
                        
                        <div style={{display: 'flex', gap: '8px', marginBottom: '15px', flexWrap: 'wrap'}}>
                          <select value={filterSchwierigkeit} onChange={(e) => setFilterSchwierigkeit(e.target.value)} style={{flex: 1, minWidth: '110px', padding: '8px', border: '2px solid #e9ecef', borderRadius: '6px', fontSize: '13px'}}>
                            <option value="">Alle Schwierigkeiten</option>
                            {[...new Set(einzelfragenListe.flatMap(f => (f.schwierigkeit_list || '').split(',')).map(s => s.trim()).filter(Boolean))].sort().map(v => <option key={v} value={v}>{v}</option>)}
                          </select>
                          <select value={filterKlasse} onChange={(e) => setFilterKlasse(e.target.value)} style={{flex: 1, minWidth: '110px', padding: '8px', border: '2px solid #e9ecef', borderRadius: '6px', fontSize: '13px'}}>
                            <option value="">Alle Klassen</option>
                            {[...new Set(einzelfragenListe.flatMap(f => (f.klasse_list || '').split(',')).map(s => s.trim()).filter(Boolean))].sort().map(v => <option key={v} value={v}>{v}</option>)}
                          </select>
                          <select value={filterFach} onChange={(e) => setFilterFach(e.target.value)} style={{flex: 1, minWidth: '110px', padding: '8px', border: '2px solid #e9ecef', borderRadius: '6px', fontSize: '13px'}}>
                            <option value="">Alle Fächer</option>
                            {[...new Set(einzelfragenListe.flatMap(f => (f.fach_list || '').split(',')).map(s => s.trim()).filter(Boolean))].sort().map(v => <option key={v} value={v}>{v}</option>)}
                          </select>
                        </div>

                        <div style={{maxHeight: '300px', overflowY: 'auto'}}>
                          {einzelfragenListe.filter(frage => {
                            const inList = (listStr, sel) => !sel || (listStr || '').split(',').map(s => s.trim()).includes(sel);
                            return (frage.question.toLowerCase().includes(fragenSuche.toLowerCase()) ||
                                    frage.type.toLowerCase().includes(fragenSuche.toLowerCase())) &&
                              inList(frage.schwierigkeit_list, filterSchwierigkeit) &&
                              inList(frage.klasse_list, filterKlasse) &&
                              inList(frage.fach_list, filterFach);
                          }).map(frage => (
                            <div 
                              key={frage.id}
                              style={{
                                background: '#f8f9fa',
                                border: '2px solid #e9ecef',
                                borderRadius: '6px',
                                padding: '15px',
                                marginBottom: '10px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onClick={() => {
                                setPendingConfirm({ type: 'frage', id: frage.id, name: frage.question.substring(0, 60) });
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.borderColor = '#28a745';
                                e.target.style.backgroundColor = '#e8f5e8';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.borderColor = '#e9ecef';
                                e.target.style.backgroundColor = '#f8f9fa';
                              }}
                            >
                              <div style={{fontWeight: 500, color: '#495057', marginBottom: '5px'}}>
                                {frage.question.length > 80 
                                  ? frage.question.substring(0, 80) + '...'
                                  : frage.question
                                }
                              </div>
                              <div style={{fontSize: '12px', color: '#6c757d'}}>
                                Typ: {frage.type || 'Unbekannt'}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Neue Frage erstellen */}
                    {questionManagerView === 'erstellen' && (
                      <div>
                        <div style={{marginBottom: '15px'}}>
                          <button 
                            onClick={() => setQuestionManagerView('main')}
                            style={{
                              background: '#6c757d',
                              color: 'white',
                              padding: '8px 15px',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            ← Zurück
                          </button>
                        </div>
                        
                        <div style={{marginBottom: '15px'}}>
                          <label style={{display: 'block', marginBottom: '5px', fontWeight: 500, color: '#495057'}}>
                            Frage-Text:
                          </label>
                          <textarea 
                            value={neueFrageForm.text}
                            onChange={(e) => setNeueFrageForm({...neueFrageForm, text: e.target.value})}
                            placeholder="Geben Sie hier Ihre Frage ein..."
                            style={{
                              width: '100%',
                              height: '80px',
                              padding: '10px',
                              border: '2px solid #e9ecef',
                              borderRadius: '6px',
                              fontSize: '14px',
                              resize: 'vertical'
                            }}
                          />
                        </div>
                        
                        <div style={{marginBottom: '15px'}}>
                          <label style={{display: 'block', marginBottom: '5px', fontWeight: 500, color: '#495057'}}>
                            Frage-Typ:
                          </label>
                          <select 
                            value={neueFrageForm.typ}
                            onChange={(e) => setNeueFrageForm({...neueFrageForm, typ: e.target.value})}
                            style={{
                              width: '100%',
                              padding: '10px',
                              border: '2px solid #e9ecef',
                              borderRadius: '6px',
                              fontSize: '14px'
                            }}
                          >
                            <option value="">Bitte wählen...</option>
                            <option value="multipleChoice">Multiple Choice</option>
                            <option value="imageQuestion">Bildfrage</option>
                            <option value="yesno">Ja/Nein</option>
                            <option value="dragdrop">Drag & Drop</option>
                            <option value="multiDragdrop">Multi Drag & Drop</option>
                          </select>
                        </div>

                        {/* Multiple Choice Felder */}
                        {neueFrageForm.typ === 'multipleChoice' && (
                          <div>
                            <div style={{marginBottom: '10px'}}>
                              <label style={{display: 'block', marginBottom: '5px', fontWeight: 500, color: '#495057'}}>Option A:</label>
                              <input 
                                type="text"
                                value={neueFrageForm.optionA}
                                onChange={(e) => setNeueFrageForm({...neueFrageForm, optionA: e.target.value})}
                                style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}
                              />
                            </div>
                            <div style={{marginBottom: '10px'}}>
                              <label style={{display: 'block', marginBottom: '5px', fontWeight: 500, color: '#495057'}}>Option B:</label>
                              <input 
                                type="text"
                                value={neueFrageForm.optionB}
                                onChange={(e) => setNeueFrageForm({...neueFrageForm, optionB: e.target.value})}
                                style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}
                              />
                            </div>
                            <div style={{marginBottom: '10px'}}>
                              <label style={{display: 'block', marginBottom: '5px', fontWeight: 500, color: '#495057'}}>Option C:</label>
                              <input 
                                type="text"
                                value={neueFrageForm.optionC}
                                onChange={(e) => setNeueFrageForm({...neueFrageForm, optionC: e.target.value})}
                                style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}
                              />
                            </div>
                            <div style={{marginBottom: '10px'}}>
                              <label style={{display: 'block', marginBottom: '5px', fontWeight: 500, color: '#495057'}}>Option D:</label>
                              <input 
                                type="text"
                                value={neueFrageForm.optionD}
                                onChange={(e) => setNeueFrageForm({...neueFrageForm, optionD: e.target.value})}
                                style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}
                              />
                            </div>
                            <div style={{marginBottom: '10px'}}>
                              <label style={{display: 'block', marginBottom: '5px', fontWeight: 500, color: '#495057'}}>Korrekte Antwort:</label>
                              <select 
                                value={neueFrageForm.correctOption}
                                onChange={(e) => setNeueFrageForm({...neueFrageForm, correctOption: e.target.value})}
                                style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}
                              >
                                <option value="">Bitte wählen...</option>
                                <option value="A">A</option>
                                <option value="B">B</option>
                                <option value="C">C</option>
                                <option value="D">D</option>
                              </select>
                            </div>
                          </div>
                        )}

                        {/* Ja/Nein Felder */}
                        {neueFrageForm.typ === 'yesno' && (
                          <div style={{marginBottom: '10px'}}>
                            <label style={{display: 'block', marginBottom: '5px', fontWeight: 500, color: '#495057'}}>Korrekte Antwort:</label>
                            <select 
                              value={neueFrageForm.yesnoAnswer}
                              onChange={(e) => setNeueFrageForm({...neueFrageForm, yesnoAnswer: e.target.value})}
                              style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}
                            >
                              <option value="">Bitte wählen...</option>
                              <option value="Ja">Ja</option>
                              <option value="Nein">Nein</option>
                            </select>
                          </div>
                        )}
                        
                        <button 
                          onClick={() => {
                            if (!neueFrageForm.text || !neueFrageForm.typ) {
                              alert('Bitte füllen Sie alle Pflichtfelder aus.');
                              return;
                            }
                            createNewQuestion();
                          }}
                          style={{
                            background: '#17a2b8',
                            color: 'white',
                            padding: '12px 20px',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            width: '100%',
                            marginTop: '15px'
                          }}
                        >
                          Frage erstellen und hinzufügen
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bestätigung: Stunde beenden */}
      {showSuccessConfirm && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff', borderRadius: '16px', padding: '32px 36px',
            maxWidth: '440px', width: '90%', boxShadow: '0 8px 40px rgba(0,0,0,0.3)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '12px' }}>🏆</div>
            <div style={{ fontWeight: 700, fontSize: '20px', marginBottom: '10px', color: '#333' }}>
              Stunde beenden?
            </div>
            <div style={{ color: '#666', marginBottom: '24px', fontSize: '15px', lineHeight: '1.5' }}>
              Möchtest du die Stunde wirklich beenden und die Pokalseite öffnen?
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => setShowSuccessConfirm(false)}
                style={{
                  padding: '10px 24px', borderRadius: '8px', border: '1px solid #ccc',
                  background: '#f5f5f5', cursor: 'pointer', fontSize: '15px', fontWeight: 500
                }}
              >
                Abbrechen
              </button>
              <button
                onClick={() => {
                  setShowSuccessConfirm(false);
                  applyViewChange('success');
                }}
                style={{
                  padding: '10px 24px', borderRadius: '8px', border: 'none',
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5a623 100%)',
                  color: '#fff', cursor: 'pointer', fontSize: '15px', fontWeight: 700,
                  boxShadow: '0 4px 12px rgba(245,166,35,0.4)'
                }}
              >
                🏆 Ja, beenden
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Non-blocking confirmation modal */}
      {pendingConfirm && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff', borderRadius: '10px', padding: '28px 32px',
            maxWidth: '420px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.25)'
          }}>
            <div style={{ fontWeight: 600, fontSize: '16px', marginBottom: '10px' }}>
              {pendingConfirm.type === 'stapel' ? 'Stapel hinzufügen?' : 'Frage hinzufügen?'}
            </div>
            <div style={{ color: '#555', marginBottom: '20px', wordBreak: 'break-word' }}>
              „{pendingConfirm.name}"
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setPendingConfirm(null)}
                style={{
                  padding: '8px 18px', borderRadius: '6px', border: '1px solid #ccc',
                  background: '#f5f5f5', cursor: 'pointer', fontSize: '14px'
                }}
              >
                Abbrechen
              </button>
              <button
                onClick={() => {
                  const confirm = pendingConfirm;
                  setPendingConfirm(null);
                  if (confirm.type === 'stapel') {
                    addStapelToSession(confirm.id, confirm.name);
                  } else {
                    addQuestionToSession(confirm.id);
                  }
                }}
                style={{
                  padding: '8px 18px', borderRadius: '6px', border: 'none',
                  background: '#007bff', color: '#fff', cursor: 'pointer', fontSize: '14px'
                }}
              >
                Hinzufügen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fragen-Browser: Nur-Mod-Vorschau aller Session-Fragen */}
      {showQuestionBrowser && (
        <div className="questionManagerOverlay">
          <div className="questionManagerPanel" style={{ maxWidth: '900px' }}>
            <div className="questionManagerHeader">
              <h2>Fragen-Übersicht (nur für Mod)</h2>
              <button className="closeButton" onClick={() => { setShowQuestionBrowser(false); setBrowserSelectedQuestion(null); }}>✕</button>
            </div>
            <div style={{ display: 'flex', gap: '20px', padding: '20px', minHeight: '400px' }}>
              {/* Linke Spalte: Fragenliste */}
              <div style={{ flex: '0 0 300px', overflowY: 'auto', maxHeight: '500px', borderRight: '1px solid #e9ecef', paddingRight: '16px' }}>
                <h4 style={{ marginTop: 0, color: '#495057', fontSize: '14px' }}>Alle Fragen ({questions.filter(q => !q.disabled).length} aktiv)</h4>
                {questions.map((q, i) => (
                  <div
                    key={q.id}
                    onClick={() => !q.disabled && setBrowserSelectedQuestion(q)}
                    style={{
                      padding: '10px 12px',
                      marginBottom: '6px',
                      borderRadius: '6px',
                      border: browserSelectedQuestion?.id === q.id ? '2px solid #007bff' : '1px solid #dee2e6',
                      background: q.disabled ? '#f0f0f0' : browserSelectedQuestion?.id === q.id ? '#e8f0fe' : 'white',
                      cursor: q.disabled ? 'not-allowed' : 'pointer',
                      opacity: q.disabled ? 0.5 : 1,
                      fontSize: '13px',
                    }}
                  >
                    <span style={{ color: '#6c757d', marginRight: '6px', fontSize: '11px' }}>#{i + 1}</span>
                    {q.disabled && <span style={{ color: '#dc3545', fontSize: '11px', marginRight: '4px' }}>🚫</span>}
                    {q.question}
                  </div>
                ))}
              </div>

              {/* Rechte Spalte: Vorschau */}
              <div style={{ flex: 1, overflowY: 'auto', maxHeight: '500px' }}>
                {browserSelectedQuestion ? (
                  <div>
                    <h4 style={{ marginTop: 0, color: '#343a40' }}>{browserSelectedQuestion.question}</h4>
                    <div style={{ marginTop: '12px' }}>
                      {renderBrowserQuestionPreview(browserSelectedQuestion)}
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', color: '#6c757d', fontSize: '14px' }}>
                    ← Frage auswählen um Vorschau zu sehen
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
